import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import { MarketingAnalyticsPanel, SiteAnalyticsPanel } from './SiteAnalytics';
import { CampaignDetailsModal, RecipientPickerModal } from './CampaignRecipients';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { externalUrl, socialUrl } from './externalUrl';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ''}/api`;
const ADMIN_PASSWORD = process.env.REACT_APP_ONBOARD_PW;

type Attachment = {
  _id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
};

type Client = {
  _id: string;
  clientName: string;
  businessName: string;
  email: string;
  phone: string;
  website: string;
  businessType: string;
  location: string;
  bio: string;
  servicesOffered: string[];
  audience: string;
  goals: string;
  offers: string;
  colorScheme: string;
  domainName: string;
  domainStatus: string;
  domainDetails: string;
  pageNames: string;
  socialInstagram: string;
  socialTiktok: string;
  socialYoutube: string;
  socialFacebook: string;
  socialTwitter: string;
  socialOther: string;
  references: string;
  notes: string;
  attachments: Attachment[];
  logoAttachments: Attachment[];
  createdAt: string;
};

type Agreement = {
  _id: string;
  planType: 'one_time' | 'monthly' | 'custom';
  amount: number;
  clientName: string;
  clientAddress: string;
  clientEmail: string;
  jurisdiction: string;
  effectiveDate: string;
};

const PLAN_LABELS: Record<Agreement['planType'], string> = {
  one_time: 'One-Time Payment',
  monthly: 'Monthly Subscription',
  custom: 'Custom / Negotiated'
};

type WebsiteClient = {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  address: string;
  socialMediaLinks: string;
  businessType: string;
  website: string;
  hasExistingWebsite?: boolean;
  logo?: { mimeType?: string } | null;
  websiteReviewSentAt?: string | null;
  marketingEmailSentAt?: string | null;
  paymentReminderSentAt?: string | null;
  subscriptionReminderSentAt?: string | null;
  lastCustomEmailSentAt?: string | null;
  paid?: boolean;
  paidAt?: string | null;
  onboardingEmailSentAt?: string | null;
  // Set when this client matches a lead — see syncClientLeadsIntoWebsiteClients.
  leadStatus?: 'closed' | 'onboarding_sent' | 'onboarded' | null;
  createdAt: string;
};

const LEAD_STATUS_BADGES: Record<NonNullable<WebsiteClient['leadStatus']>, { label: string; bg: string }> = {
  closed: { label: 'Closed Lead', bg: 'primary' },
  onboarding_sent: { label: 'Onboarding Sent', bg: 'warning' },
  onboarded: { label: 'Onboarded', bg: 'success' },
};

const SUBSCRIBER_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100, 200];
const AUDIT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100, 200];
const LEADS_ON_NEWSLETTER_PAGE_DEFAULT_SIZE = 10;

const INDUSTRY_OPTIONS = [
  'Restaurant / Food / Bar',
  'Hospitality',
  'Entertainment',
  'Tech',
  'Finance',
  'Plumbing / Electricity / HVAC',
  'Marketing',
  'Cars',
  'Real Estate',
  'Property Maintenance',
  'Wholesale',
  'Beauty / Hair',
  'Healthcare',
  'Construction'
];

const emptyWebsiteClientForm = {
  name: '',
  email: '',
  phone: '',
  address: '',
  socialMediaLinks: '',
  businessType: '',
  website: '',
  hasExistingWebsite: true,
  paid: false
};

type AuditChannelResult = {
  url?: string;
  reachable?: boolean;
  status?: number;
  blocked?: boolean;
  findings?: string[];
};

type AuditChecks = {
  website?: AuditChannelResult | null;
  instagram?: AuditChannelResult | null;
  facebook?: AuditChannelResult | null;
  googleBusiness?: AuditChannelResult | null;
};

type Subscriber = {
  _id: string;
  email: string;
  name?: string;
  phone?: string;
  businessName?: string;
  socialUrl?: string;
  googleBusinessUrl?: string;
  beats: boolean;
  mixing: boolean;
  visuals: boolean;
  web: boolean;
  ads: boolean;
  loopsTemplates: boolean;
  freemockups?: boolean;
  freeaudit?: boolean;
  projectUrl?: string;
  website?: string;
  instagram?: string;
  facebook?: string;
  auditStatus?: 'pending' | 'complete' | 'failed';
  auditScore?: 'Good' | 'Needs Work' | 'Poor';
  auditSummary?: string;
  auditFindings?: string[];
  auditChecks?: AuditChecks;
  auditRequestedAt?: string;
  auditCompletedAt?: string;
  createdAt: string;
};

type ContactSubmission = {
  _id: string;
  name?: string;
  email: string;
  phone?: string;
  socialUrl?: string;
  message?: string;
  beats: boolean;
  visuals: boolean;
  web: boolean;
  ads: boolean;
  createdAt: string;
};

type MockupRequest = {
  _id: string;
  businessName?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  instagram?: string;
  googleBusinessUrl?: string;
  createdAt: string;
};

const INTEREST_FIELDS: { key: 'beats' | 'mixing' | 'loopsTemplates' | 'visuals' | 'web' | 'ads'; label: string }[] = [
  { key: 'beats', label: 'Beats' },
  { key: 'mixing', label: 'Mixing' },
  { key: 'loopsTemplates', label: 'Loops & Templates' },
  { key: 'visuals', label: 'Visuals' },
  { key: 'web', label: 'Web' },
  { key: 'ads', label: 'Ads' }
];

type SubscriberInterest = (typeof INTEREST_FIELDS)[number]['key'];
type SubscriberInterestSet = Record<SubscriberInterest, boolean>;

// Interests picked for a whole import (file or paste) — added on top of any
// interest columns the rows already have; never removes one.
const applyImportInterests = <T extends Record<string, any>>(rows: T[], interests: SubscriberInterestSet) =>
  rows.map((row) => {
    const merged: Record<string, any> = { ...row };
    INTEREST_FIELDS.forEach(({ key }) => {
      merged[key] = Boolean(row[key]) || interests[key];
    });
    return merged as T & SubscriberInterestSet;
  });

// Row of interest checkboxes shared by the file-import popup and the paste box.
const ImportInterestPicker = ({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: SubscriberInterestSet;
  onChange: (next: SubscriberInterestSet) => void;
}) => (
  <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', alignItems: 'center' }}>
    {INTEREST_FIELDS.map((field) => (
      <Form.Check
        key={field.key}
        id={`${idPrefix}-${field.key}`}
        type="checkbox"
        label={field.label}
        checked={value[field.key]}
        onChange={(e) => onChange({ ...value, [field.key]: e.target.checked })}
      />
    ))}
  </div>
);

const emptySubscriberInterests = {
  beats: false,
  mixing: false,
  loopsTemplates: false,
  visuals: false,
  web: false,
  ads: false
};

const subscriberInterestLabel = (subscriber: Subscriber) => {
  const interests = INTEREST_FIELDS.filter((field) => subscriber[field.key]).map((field) => field.label);
  return interests.length ? interests.join(', ') : '—';
};

// ── Newsletter campaigns / contact / analytics ──────────────────────────────

type NewsletterCategory = 'beats' | 'mixing' | 'loopsTemplates' | 'web' | 'ads';

const NEWSLETTER_CATEGORY_LABELS: Record<NewsletterCategory, string> = {
  beats: 'Beats',
  mixing: 'Mixing',
  loopsTemplates: 'Loops & Templates',
  web: 'Web Development',
  ads: 'Ads'
};

const NEWSLETTER_CATEGORIES: NewsletterCategory[] = ['beats', 'mixing', 'loopsTemplates', 'web', 'ads'];

// Categories a subscriber currently qualifies for — used to default the
// Contact panel's category picker.
const subscriberCategories = (subscriber: Subscriber): NewsletterCategory[] =>
  NEWSLETTER_CATEGORIES.filter((key) => subscriber[key]);

type TemplatePreset = { label: string; subject: string; bodyText: string; ctaLabel?: string; ctaUrl?: string; imageUrl?: string };

// Only "beats" has named presets today — every other category gets a single
// free-form "Custom Message" composer.
// Free product emails — each links to its own page with ?subscriber=1, which
// skips the signup box there (they're already on the list) and shows the
// download button. Image paths are pre-encoded (%20 / %26) because the email
// renderer drops imageUrl straight into the <img src>.
const PRODUCT_EMAIL_PRESETS: Record<string, TemplatePreset> = {
  'free-vocal-template': {
    label: 'Free R&B Vocal Template (Logic)',
    subject: 'Free R&B vocal template for Logic Pro X 🎙️',
    bodyText:
      "Hey (name), here's a free R&B vocal template for Logic Pro X — a full lead vocal chain plus FX buses for parallel saturation, chorus doubler, 1/2 and 1/4 note delays, and plate and room verb.\n\nIt comes in two versions: one built on Logic's stock plugins and one for UAD & Waves users. Grab it free below and load it right onto an audio track.",
    ctaLabel: 'Get the Vocal Template',
    ctaUrl: `${window.location.origin}/templates?subscriber=1`,
    imageUrl: `${window.location.origin}/templates/r%26b_vocal/rnb-vocal-template.jpg`
  },
  'free-plugin-vocal-sauce': {
    label: 'Free Plugin: Enigma Vocal Sauce',
    subject: 'Free vocal chain plugin: Enigma Vocal Sauce 🎤',
    bodyText:
      "Hey (name), I just released Enigma Vocal Sauce — a free vocal chain in one plugin.\n\nPreamp, a 5-band EQ, 1176-style and LA-2A-style compression, a Pultec-style EQ for weight and air, and a de-esser, all in one window. Put it on your vocal and you're most of the way there. Download it free below.",
    ctaLabel: 'Download Vocal Sauce',
    ctaUrl: `${window.location.origin}/plugins?subscriber=1#vocal-sauce`,
    imageUrl: `${window.location.origin}/plugins/Vocal%20Sauce/Enigma%20Vocal%20Sauce.png`
  },
  'free-plugin-texture-console': {
    label: 'Free Plugin: Enigma Texture Console',
    subject: 'Free Chroma Console-style plugin: Enigma Texture Console 🎛️',
    bodyText:
      "Hey (name), I just released Enigma Texture Console — a free, Chroma Console-style texture plugin.\n\nCharacter (drive, sweeten, crush, cassette), movement (chorus, phaser, tremolo, vibrato), diffusion (echo, space, tape lag, cascade), and degrade, plus filter and reverb — all in one effect. Download it free below.",
    ctaLabel: 'Download Texture Console',
    ctaUrl: `${window.location.origin}/plugins?subscriber=1#texture-console`,
    imageUrl: `${window.location.origin}/plugins/Texture%20Console/TextureConsoleV32.png`
  },
  // Stemcell is a web app, not a download — the button goes straight to it.
  'free-synth-stemcell': {
    label: 'Free Synth: Stemcell (online granular synth)',
    subject: 'Free online granular synth: Stemcell 🧬',
    bodyText:
      "Hey (name), I just launched Stemcell — a free granular synth that runs right in your browser. Nothing to download or install.\n\nRecord your mic or load any sound, drag across the waveform to pick a region, and Stemcell breaks it into grains you can stretch, scatter, pitch, and play from your computer keyboard. Hit a mutation like Frozen Memory, Dream Choir, or Glass Rain, or press Surprise Me and see what grows. Perfect for pads, drones, and textures. Try it free below.",
    ctaLabel: 'Try Stemcell Free',
    ctaUrl: 'https://stemcell.enigma-labs.com',
    imageUrl: `${window.location.origin}/stemcell%20synth.png`
  }
};

const TEMPLATE_PRESETS: Record<NewsletterCategory, Record<string, TemplatePreset>> = {
  beats: {
    'new-beats-dropped': {
      label: 'New Beats Dropped',
      subject: 'New beats just dropped 🔥',
      bodyText: "Hey (name), just dropped some new beats — thought you might like these. Take a listen below!",
      ctaLabel: 'Listen on BeatStars',
      ctaUrl: 'https://www.beatstars.com/genwav'
    },
    'discount-announcement': {
      label: 'Discount Announcement',
      subject: 'Limited-time discount on beats 🎁',
      bodyText: "Hey (name), running a limited-time discount on beats right now — grab something before it ends!",
      ctaLabel: 'Shop the Discount',
      ctaUrl: 'https://www.beatstars.com/genwav'
    },
    'custom-beats-for-you': {
      label: 'Custom Beats For You',
      subject: 'I made some beats for you 🎧',
      bodyText:
        "Hey (name), I love what you've been putting out and I wanted to send you some beats I think you'd like! Let me know what you think, or if you want to work on something custom I can do that as well.\n\nBest,\n\nGen, Enigma Labs",
      ctaLabel: 'Listen on BeatStars',
      ctaUrl: 'https://www.beatstars.com/genwav'
    },
    'custom-message': { label: 'Custom Message', subject: '', bodyText: '' }
  },
  mixing: {
    'free-plugin-vocal-sauce': PRODUCT_EMAIL_PRESETS['free-plugin-vocal-sauce'],
    'free-vocal-template': PRODUCT_EMAIL_PRESETS['free-vocal-template'],
    'free-synth-stemcell': PRODUCT_EMAIL_PRESETS['free-synth-stemcell'],
    'custom-message': { label: 'Custom Message', subject: '', bodyText: '' }
  },
  loopsTemplates: {
    'free-sample-pack': {
      label: 'Free Sample Pack',
      subject: 'A free sample pack for you 🎁',
      bodyText: "Hey (name), thanks again for signing up! Here's a free sample pack to get you started — grab it below, and check out more loops and templates on BeatStars.",
      ctaLabel: 'Get the Free Sample Pack',
      ctaUrl: 'https://www.beatstars.com/genwav',
      imageUrl: `${window.location.origin}/wav-pack-vol1.jpg`
    },
    'free-vocal-template': PRODUCT_EMAIL_PRESETS['free-vocal-template'],
    'free-plugin-vocal-sauce': PRODUCT_EMAIL_PRESETS['free-plugin-vocal-sauce'],
    'free-plugin-texture-console': PRODUCT_EMAIL_PRESETS['free-plugin-texture-console'],
    'free-synth-stemcell': PRODUCT_EMAIL_PRESETS['free-synth-stemcell'],
    'custom-message': { label: 'Custom Message', subject: '', bodyText: '' }
  },
  web: { 'custom-message': { label: 'Custom Message', subject: '', bodyText: '' } },
  ads: { 'custom-message': { label: 'Custom Message', subject: '', bodyText: '' } }
};

type NewsletterSend = {
  _id: string;
  subscriberId: string | { _id: string; email: string; name?: string };
  campaignId?: string;
  category: NewsletterCategory | 'signup';
  templateKey?: string;
  subject: string;
  html: string;
  opened: boolean;
  openedAt?: string;
  clicked: boolean;
  clickedAt?: string;
  sentAt: string;
};

type NewsletterCampaign = {
  _id: string;
  category: NewsletterCategory;
  templateKey: string;
  subject: string;
  html: string;
  ctaLabel?: string;
  ctaUrl?: string;
  imageUrl?: string;
  recipientCount: number;
  scheduledAt?: string;
  createdAt: string;
};

type SendStats = { sent: number; opened: number; clicked: number; openRate: number; clickRate: number };

type OutreachAnalytics = {
  personal: SendStats;
  signup: SendStats;
  campaignsSummary: SendStats & { totalCampaigns: number };
  campaigns: (SendStats & { id: string; category?: string; subject?: string; sentAt?: string; recipientCount?: number })[];
};

type BlogPostRecord = {
  _id: string;
  Title: string;
  Author: string;
  Category?: string;
  Excerpt?: string;
  Body: string;
  datee: string;
  Image?: string;
};

type FileAttachment = { filename: string; content: string };

function readFileAsAttachment(file: File): Promise<FileAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.split(',')[1] || '';
      resolve({ filename: file.name, content: base64 });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type PastedSubscriber = { email: string; name: string; phone: string; businessName: string; socialUrl: string };

// Excel/Sheets pastes are tab-separated when copied as a range; fall back to
// comma-separated for plain-text pastes.
const splitSubscriberLine = (line: string): string[] =>
  (line.includes('\t') ? line.split('\t') : line.split(',')).map((cell) => cell.trim());

// Some exported sheets include a lone "Genre" section/divider row between the
// header and the real data (or between blocks of rows) — it isn't a
// subscriber, just a label, recognizable by mentioning "genre" while having
// almost no other content.
const isGenreMarkerRow = (row: string[]) =>
  row.some((cell) => /genre/i.test(cell)) && row.filter(Boolean).length <= 2;

const parsePastedSubscribers = (text: string): PastedSubscriber[] => {
  const rows = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map(splitSubscriberLine)
    .filter((row) => !isGenreMarkerRow(row));

  if (!rows.length) return [];

  const headerKeywords = /email|name|phone|business|instagram|social/i;
  const looksLikeHeader = rows[0].some((cell) => headerKeywords.test(cell));

  let emailIdx = 0;
  let nameIdx = -1;
  let phoneIdx = -1;
  let businessIdx = -1;
  let socialIdx = -1;

  if (looksLikeHeader) {
    rows[0].forEach((cell, idx) => {
      const c = cell.toLowerCase();
      if (c.includes('email')) emailIdx = idx;
      else if (c.includes('instagram') || c.includes('social')) socialIdx = idx;
      else if (c.includes('business')) businessIdx = idx;
      else if (c.includes('phone')) phoneIdx = idx;
      else if (c.includes('name')) nameIdx = idx;
    });
  } else {
    [emailIdx, nameIdx, phoneIdx, businessIdx, socialIdx] = [0, 1, 2, 3, 4];
  }

  const dataRows = looksLikeHeader ? rows.slice(1) : rows;

  return dataRows
    .filter((row) => !isGenreMarkerRow(row))
    .map((row) => ({
      email: (row[emailIdx] || '').trim(),
      name: nameIdx >= 0 ? (row[nameIdx] || '').trim() : '',
      phone: phoneIdx >= 0 ? (row[phoneIdx] || '').trim() : '',
      businessName: businessIdx >= 0 ? (row[businessIdx] || '').trim() : '',
      socialUrl: socialIdx >= 0 ? (row[socialIdx] || '').trim() : ''
    }))
    .filter((row) => row.email.includes('@'));
};

const Admin = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [websiteClients, setWebsiteClients] = useState<WebsiteClient[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingAgreements, setLoadingAgreements] = useState(true);
  const [loadingWebsiteClients, setLoadingWebsiteClients] = useState(true);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  const [contactSubmissions, setContactSubmissions] = useState<ContactSubmission[]>([]);
  const [loadingContactSubmissions, setLoadingContactSubmissions] = useState(true);
  const [mockupRequests, setMockupRequests] = useState<MockupRequest[]>([]);
  const [loadingMockupRequests, setLoadingMockupRequests] = useState(true);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(AUDIT_PAGE_SIZE_OPTIONS[0]);
  const [runningAuditId, setRunningAuditId] = useState<string | null>(null);
  const [expandedAuditId, setExpandedAuditId] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [editingWebsiteClientId, setEditingWebsiteClientId] = useState<string | null>(null);
  const [websiteClientEditForm, setWebsiteClientEditForm] = useState(emptyWebsiteClientForm);
  const [showAddWebsiteClient, setShowAddWebsiteClient] = useState(false);
  const [newWebsiteClient, setNewWebsiteClient] = useState(emptyWebsiteClientForm);
  const [selectedWebsiteClientIds, setSelectedWebsiteClientIds] = useState<Set<string>>(new Set());
  const [sendingReview, setSendingReview] = useState(false);
  const [sendingMarketingEmail, setSendingMarketingEmail] = useState(false);
  const [sendingPaymentReminder, setSendingPaymentReminder] = useState(false);
  const [sendingSubscriptionReminder, setSendingSubscriptionReminder] = useState(false);
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const [showCustomClientEmail, setShowCustomClientEmail] = useState(false);
  const [customClientEmailTargetIds, setCustomClientEmailTargetIds] = useState<string[]>([]);
  const [customClientEmailSubject, setCustomClientEmailSubject] = useState('');
  const [customClientEmailBody, setCustomClientEmailBody] = useState('');

  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const BLANK_SUBSCRIBER_FORM = { email: '', name: '', phone: '', businessName: '', socialUrl: '', ...emptySubscriberInterests };
  const [newSubscriber, setNewSubscriber] = useState(BLANK_SUBSCRIBER_FORM);
  const [editingSubscriberId, setEditingSubscriberId] = useState<string | null>(null);
  const [subscriberEditForm, setSubscriberEditForm] = useState({ email: '', name: '', phone: '', businessName: '', socialUrl: '', ...emptySubscriberInterests });
  const [showPasteSubscribers, setShowPasteSubscribers] = useState(false);
  const [pasteSubscribersText, setPasteSubscribersText] = useState('');
  const [importingSubscribers, setImportingSubscribers] = useState(false);
  const [subscriberImportStatus, setSubscriberImportStatus] = useState('');
  const [pasteInterests, setPasteInterests] = useState<SubscriberInterestSet>({ ...emptySubscriberInterests });
  // A parsed file waiting in the "pick interests" popup before importing.
  const [pendingFileImport, setPendingFileImport] = useState<{ fileName: string; rows: Record<string, any>[] } | null>(null);
  const [fileImportInterests, setFileImportInterests] = useState<SubscriberInterestSet>({ ...emptySubscriberInterests });
  const subscriberFileInputRef = useRef<HTMLInputElement>(null);
  const [subscriberPageSize, setSubscriberPageSize] = useState(SUBSCRIBER_PAGE_SIZE_OPTIONS[0]);
  const [subscriberPage, setSubscriberPage] = useState(1);
  const [subscriberSearchQuery, setSubscriberSearchQuery] = useState('');
  const [subscriberInterestFilter, setSubscriberInterestFilter] = useState('any');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterError, setNewsletterError] = useState('');

  // Contact panel (per-subscriber, inline "box underneath" the row)
  const BLANK_CONTACT_FORM = { category: 'beats' as NewsletterCategory, templateKey: 'custom-message', subject: '', bodyText: '', ctaLabel: '', ctaUrl: '', imageUrl: '', resendCampaignId: '' };
  const [contactOpenId, setContactOpenId] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState(BLANK_CONTACT_FORM);
  const [contactAttachments, setContactAttachments] = useState<FileAttachment[]>([]);
  const [contactCampaigns, setContactCampaigns] = useState<NewsletterCampaign[]>([]);
  const [contactSending, setContactSending] = useState(false);

  // Per-subscriber Analytics modal
  const [analyticsSubscriber, setAnalyticsSubscriber] = useState<Subscriber | null>(null);
  const [analyticsSends, setAnalyticsSends] = useState<NewsletterSend[]>([]);
  const [loadingSubscriberAnalytics, setLoadingSubscriberAnalytics] = useState(false);

  // Newsletters (outreach analytics) — personal (one-off Contact-panel) sends vs campaign
  // blasts, shown underneath the Subscribers table.
  const [outreachAnalytics, setOutreachAnalytics] = useState<OutreachAnalytics | null>(null);
  const [loadingOutreachAnalytics, setLoadingOutreachAnalytics] = useState(true);

  // Blog — posts created here are served from the enigma database and
  // merged client-side with the static/Firestore posts on /Blog (see
  // Blog.tsx and BlogEntry.tsx). Shown underneath Newsletters.
  const BLANK_BLOG_POST_FORM = { Title: '', Category: '', Excerpt: '', Body: '', datee: '' };
  const [blogPosts, setBlogPosts] = useState<BlogPostRecord[]>([]);
  const [loadingBlogPosts, setLoadingBlogPosts] = useState(true);
  const [showAddBlogPost, setShowAddBlogPost] = useState(false);
  const [newBlogPost, setNewBlogPost] = useState(BLANK_BLOG_POST_FORM);
  const [newBlogPostImage, setNewBlogPostImage] = useState<File | null>(null);
  const [editingBlogId, setEditingBlogId] = useState<string | null>(null);
  const [blogEditForm, setBlogEditForm] = useState(BLANK_BLOG_POST_FORM);
  const [blogEditImage, setBlogEditImage] = useState<File | null>(null);
  const [savingBlogPost, setSavingBlogPost] = useState(false);
  const [blogMessage, setBlogMessage] = useState('');
  const [blogError, setBlogError] = useState('');

  // Create Campaign modal
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const BLANK_CAMPAIGN_FORM = { category: 'beats' as NewsletterCategory, templateKey: 'custom-message', subject: '', bodyText: '', ctaLabel: '', ctaUrl: '', imageUrl: '', recipientCategories: ['beats'] as NewsletterCategory[], scheduledAt: '' };
  const [campaignForm, setCampaignForm] = useState(BLANK_CAMPAIGN_FORM);
  const [campaignAttachments, setCampaignAttachments] = useState<FileAttachment[]>([]);
  const [campaignSending, setCampaignSending] = useState(false);

  // Web/ads "campaigns" bypass the newsletter-interest composer entirely —
  // they always resend the fixed cold-email pitch to matching leads,
  // filtered by whether the lead has already opened/clicked it.
  const LEAD_CAMPAIGN_CATEGORIES: NewsletterCategory[] = ['web', 'ads'];
  const [leadAudienceOpenedFilter, setLeadAudienceOpenedFilter] = useState<'all' | 'opened' | 'not_opened'>('not_opened');
  const [leadAudienceClickedFilter, setLeadAudienceClickedFilter] = useState<'all' | 'clicked' | 'not_clicked'>('not_clicked');
  const [leadAudienceCount, setLeadAudienceCount] = useState<number | null>(null);
  const [leadAudienceLoading, setLeadAudienceLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState<'all' | Agreement['planType']>('all');
  const [websiteFilter, setWebsiteFilter] = useState<'all' | 'has' | 'none'>('all');
  const [igFilter, setIgFilter] = useState<'all' | 'has' | 'none'>('all');

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/onboarding/clients`);
      if (response.data?.ok) {
        setClients(response.data.clients || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
      setError('Could not load onboarding clients.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgreements = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/agreements`);
      if (response.data?.ok) {
        setAgreements(response.data.agreements || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
      setError('Could not load signed agreements.');
    } finally {
      setLoadingAgreements(false);
    }
  };

  const fetchWebsiteClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/website-clients`);
      if (response.data?.ok) {
        setWebsiteClients(response.data.clients || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
      setError('Could not load website clients.');
    } finally {
      setLoadingWebsiteClients(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/newsletter/subscribers`);
      if (response.data?.ok) {
        setSubscribers(response.data.subscribers || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
      setNewsletterError('Could not load newsletter subscribers.');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contact/submissions`);
      if (response.data?.ok) {
        setContactSubmissions(response.data.submissions || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
    } finally {
      setLoadingContactSubmissions(false);
    }
  };

  const fetchMockupRequests = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/leads`, { params: { inbound: true, source: 'mockup_form' } });
      if (response.data?.ok) {
        setMockupRequests(response.data.leads || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
    } finally {
      setLoadingMockupRequests(false);
    }
  };

  const fetchOutreachAnalytics = async () => {
    setLoadingOutreachAnalytics(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/newsletter/outreach-analytics`);
      if (response.data?.ok) {
        setOutreachAnalytics(response.data);
      }
    } catch (fetchError) {
      console.error(fetchError);
    } finally {
      setLoadingOutreachAnalytics(false);
    }
  };

  // ── Blog ─────────────────────────────────────────────────────────────────

  const fetchBlogPosts = async () => {
    setLoadingBlogPosts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/blog`);
      if (response.data?.ok) {
        setBlogPosts(response.data.posts || []);
      }
    } catch (fetchError) {
      console.error(fetchError);
      setBlogError('Could not load blog posts.');
    } finally {
      setLoadingBlogPosts(false);
    }
  };

  const blogPostFormData = (form: typeof BLANK_BLOG_POST_FORM, image: File | null) => {
    const data = new FormData();
    data.append('Title', form.Title);
    data.append('Category', form.Category);
    data.append('Excerpt', form.Excerpt);
    data.append('Body', form.Body);
    if (form.datee) data.append('datee', form.datee);
    if (image) data.append('coverImage', image);
    return data;
  };

  const handleAddBlogPost = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newBlogPost.Title.trim() || !newBlogPost.Body.trim()) {
      setBlogError('Title and body are required.');
      return;
    }
    setSavingBlogPost(true);
    setBlogError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/blog`, blogPostFormData(newBlogPost, newBlogPostImage));
      if (response.data?.ok) {
        setBlogMessage('Post published.');
        setNewBlogPost(BLANK_BLOG_POST_FORM);
        setNewBlogPostImage(null);
        setShowAddBlogPost(false);
        fetchBlogPosts();
      } else {
        setBlogError(response.data?.message || 'Could not publish the post.');
      }
    } catch (saveError) {
      console.error(saveError);
      setBlogError('Could not publish the post.');
    } finally {
      setSavingBlogPost(false);
    }
  };

  const handleStartEditBlogPost = (post: BlogPostRecord) => {
    setShowAddBlogPost(false);
    setEditingBlogId(post._id);
    setBlogEditForm({
      Title: post.Title || '',
      Category: post.Category || '',
      Excerpt: post.Excerpt || '',
      Body: post.Body || '',
      datee: post.datee || ''
    });
    setBlogEditImage(null);
  };

  const handleCancelEditBlogPost = () => {
    setEditingBlogId(null);
    setBlogEditForm(BLANK_BLOG_POST_FORM);
    setBlogEditImage(null);
  };

  const handleSaveBlogPost = async (postId: string) => {
    if (!blogEditForm.Title.trim() || !blogEditForm.Body.trim()) {
      setBlogError('Title and body are required.');
      return;
    }
    setSavingBlogPost(true);
    setBlogError('');
    try {
      const response = await axios.put(`${API_BASE_URL}/blog/${postId}`, blogPostFormData(blogEditForm, blogEditImage));
      if (response.data?.ok) {
        setBlogMessage('Post updated.');
        handleCancelEditBlogPost();
        fetchBlogPosts();
      } else {
        setBlogError(response.data?.message || 'Could not update the post.');
      }
    } catch (saveError) {
      console.error(saveError);
      setBlogError('Could not update the post.');
    } finally {
      setSavingBlogPost(false);
    }
  };

  const handleDeleteBlogPost = async (postId: string) => {
    const confirmDelete = window.confirm('Delete this blog post?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/blog/${postId}`);
      setBlogMessage('Post deleted.');
      fetchBlogPosts();
    } catch (deleteError) {
      console.error(deleteError);
      setBlogError('Could not delete the post.');
    }
  };

  const handleRunAudit = async (subscriberId: string) => {
    setRunningAuditId(subscriberId);
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/subscribers/${subscriberId}/run-audit`);
      if (response.data?.ok) {
        setSubscribers((prev) => prev.map((s) => (s._id === subscriberId ? response.data.subscriber : s)));
      }
    } catch (runError) {
      console.error(runError);
      setNewsletterError('Could not run the audit.');
    } finally {
      setRunningAuditId(null);
    }
  };

  const handleAddSubscriber = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newSubscriber.email) {
      setNewsletterError('Email is required to add a subscriber.');
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/subscribers`, newSubscriber);
      if (response.data?.ok) {
        setNewsletterMessage(response.data.duplicate ? 'A subscriber with this email already exists.' : 'Subscriber added.');
        setNewSubscriber(BLANK_SUBSCRIBER_FORM);
        setShowAddSubscriber(false);
        fetchSubscribers();
      } else {
        setNewsletterError(response.data?.message || 'Could not add subscriber.');
      }
    } catch (addError) {
      console.error(addError);
      setNewsletterError('Could not add subscriber.');
    }
  };

  const handlePasteSubscribers = async () => {
    const parsed = parsePastedSubscribers(pasteSubscribersText);
    if (!parsed.length) {
      setNewsletterError('Could not detect any subscribers in the pasted text.');
      return;
    }
    setImportingSubscribers(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/subscribers/import-bulk`, {
        subscribers: applyImportInterests(parsed, pasteInterests),
      });
      if (response.data?.ok) {
        const { created = 0, updated = 0, skipped = 0 } = response.data;
        setNewsletterMessage(
          `Imported ${created} new subscriber${created === 1 ? '' : 's'}` +
            (updated ? `, added new info to ${updated} existing` : '') +
            (skipped ? ` — skipped ${skipped} (duplicate with nothing new, or missing email)` : '') +
            '.'
        );
        setPasteSubscribersText('');
        setPasteInterests({ ...emptySubscriberInterests });
        setShowPasteSubscribers(false);
        fetchSubscribers();
      } else {
        setNewsletterError(response.data?.message || 'Could not import subscribers.');
      }
    } catch (importError) {
      console.error(importError);
      setNewsletterError('Could not import subscribers.');
    } finally {
      setImportingSubscribers(false);
    }
  };

  // ── Contact panel ────────────────────────────────────────────────────────

  const loadCampaignsForCategory = async (category: NewsletterCategory) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/newsletter/campaigns`, { params: { category } });
      setContactCampaigns(response.data?.campaigns || []);
    } catch (loadError) {
      console.error(loadError);
      setContactCampaigns([]);
    }
  };

  const handleOpenContact = (subscriber: Subscriber) => {
    if (contactOpenId === subscriber._id) {
      setContactOpenId(null);
      return;
    }
    const defaultCategory = subscriberCategories(subscriber)[0] || 'beats';
    const preset = TEMPLATE_PRESETS[defaultCategory]['custom-message'];
    setContactForm({ ...BLANK_CONTACT_FORM, category: defaultCategory, subject: preset.subject, bodyText: preset.bodyText });
    setContactAttachments([]);
    setAnalyticsSubscriber(null); // don't show two overlapping subscriber panels at once
    setContactOpenId(subscriber._id);
    loadCampaignsForCategory(defaultCategory);
  };

  const handleContactCategoryChange = (category: NewsletterCategory) => {
    const templateKey = 'custom-message';
    const preset = TEMPLATE_PRESETS[category][templateKey];
    setContactForm({ ...contactForm, category, templateKey, subject: preset.subject, bodyText: preset.bodyText, ctaLabel: preset.ctaLabel || '', ctaUrl: preset.ctaUrl || '', imageUrl: preset.imageUrl || '', resendCampaignId: '' });
    loadCampaignsForCategory(category);
  };

  const handleContactTemplateChange = (templateKey: string) => {
    const preset = TEMPLATE_PRESETS[contactForm.category][templateKey];
    setContactForm({ ...contactForm, templateKey, subject: preset.subject, bodyText: preset.bodyText, ctaLabel: preset.ctaLabel || '', ctaUrl: preset.ctaUrl || '', imageUrl: preset.imageUrl || '', resendCampaignId: '' });
  };

  const handleContactResendCampaignChange = (campaignId: string) => {
    if (!campaignId) {
      setContactForm({ ...contactForm, resendCampaignId: '' });
      return;
    }
    const campaign = contactCampaigns.find((c) => c._id === campaignId);
    setContactForm({
      ...contactForm,
      resendCampaignId: campaignId,
      subject: campaign?.subject || contactForm.subject,
      bodyText: campaign?.html || contactForm.bodyText
    });
  };

  const handleContactAttachmentChange = async (files: FileList | null) => {
    if (!files || !files.length) return;
    const read = await Promise.all(Array.from(files).map(readFileAsAttachment));
    setContactAttachments(read);
  };

  const handleSendContact = async (subscriber: Subscriber) => {
    setContactSending(true);
    try {
      const payload = contactForm.resendCampaignId
        ? { resendCampaignId: contactForm.resendCampaignId }
        : {
            category: contactForm.category,
            templateKey: contactForm.templateKey,
            subject: contactForm.subject,
            bodyText: contactForm.bodyText,
            ctaLabel: contactForm.ctaLabel,
            ctaUrl: contactForm.ctaUrl,
            imageUrl: contactForm.imageUrl,
            attachments: contactAttachments
          };
      const response = await axios.post(`${API_BASE_URL}/newsletter/subscribers/${subscriber._id}/send`, payload);
      if (response.data?.ok) {
        setNewsletterMessage(`Email sent to ${subscriber.email}.`);
        setContactOpenId(null);
      } else {
        setNewsletterError(response.data?.message || 'Could not send email.');
      }
    } catch (sendError: any) {
      setNewsletterError(sendError?.response?.data?.message || 'Could not send email.');
    } finally {
      setContactSending(false);
    }
  };

  // ── Per-subscriber analytics ────────────────────────────────────────────

  const handleOpenSubscriberAnalytics = async (subscriber: Subscriber) => {
    setContactOpenId(null);
    setAnalyticsSubscriber(subscriber);
    setLoadingSubscriberAnalytics(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/newsletter/subscribers/${subscriber._id}/sends`);
      setAnalyticsSends(response.data?.sends || []);
    } catch (loadError) {
      console.error(loadError);
      setAnalyticsSends([]);
    } finally {
      setLoadingSubscriberAnalytics(false);
    }
  };

  // ── Create Campaign ──────────────────────────────────────────────────────

  const handleOpenCreateCampaign = () => {
    const preset = TEMPLATE_PRESETS.beats['custom-message'];
    setCampaignForm({ ...BLANK_CAMPAIGN_FORM, subject: preset.subject, bodyText: preset.bodyText });
    setCampaignAttachments([]);
    setCampaignRecipientIds(null);
    setShowCreateCampaign(true);
  };

  const handleCampaignCategoryChange = (category: NewsletterCategory) => {
    const templateKey = 'custom-message';
    const preset = TEMPLATE_PRESETS[category][templateKey];
    setCampaignForm({ ...campaignForm, category, templateKey, subject: preset.subject, bodyText: preset.bodyText, ctaLabel: preset.ctaLabel || '', ctaUrl: preset.ctaUrl || '', imageUrl: preset.imageUrl || '', recipientCategories: [category] });
    setCampaignRecipientIds(null);
  };

  const handleCampaignTemplateChange = (templateKey: string) => {
    const preset = TEMPLATE_PRESETS[campaignForm.category][templateKey];
    setCampaignForm({ ...campaignForm, templateKey, subject: preset.subject, bodyText: preset.bodyText, ctaLabel: preset.ctaLabel || '', ctaUrl: preset.ctaUrl || '', imageUrl: preset.imageUrl || '' });
  };

  const toggleCampaignRecipientCategory = (category: NewsletterCategory) => {
    // Changing interests goes back to "everyone matching" — a hand-picked
    // list would silently stop matching what the checkboxes say.
    setCampaignRecipientIds(null);
    setCampaignForm((prev) => {
      const has = prev.recipientCategories.includes(category);
      const recipientCategories = has
        ? prev.recipientCategories.filter((c) => c !== category)
        : [...prev.recipientCategories, category];
      return { ...prev, recipientCategories };
    });
  };

  const handleCampaignAttachmentChange = async (files: FileList | null) => {
    if (!files || !files.length) return;
    const read = await Promise.all(Array.from(files).map(readFileAsAttachment));
    setCampaignAttachments(read);
  };

  // Who a campaign goes to: everyone matching the picked interests, unless the
  // recipient window was used to choose people by hand (campaignRecipientIds).
  const [campaignRecipientIds, setCampaignRecipientIds] = useState<string[] | null>(null);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [detailsCampaignId, setDetailsCampaignId] = useState<string | null>(null);
  const matchingRecipientIds = useMemo(
    () =>
      subscribers
        .filter((s) => (s.email || '').trim() && campaignForm.recipientCategories.some((c) => s[c]))
        .map((s) => s._id),
    [subscribers, campaignForm.recipientCategories]
  );
  const effectiveRecipientIds = campaignRecipientIds ?? matchingRecipientIds;
  const campaignRecipientCount = effectiveRecipientIds.length;

  const isLeadCampaign = LEAD_CAMPAIGN_CATEGORIES.includes(campaignForm.category);

  useEffect(() => {
    if (!showCreateCampaign || !isLeadCampaign) {
      setLeadAudienceCount(null);
      return;
    }
    let cancelled = false;
    setLeadAudienceLoading(true);
    axios
      .get(`${API_BASE_URL}/crm/leads/audience-count`, {
        params: { category: campaignForm.category, openedFilter: leadAudienceOpenedFilter, clickedFilter: leadAudienceClickedFilter }
      })
      .then((response) => {
        if (!cancelled && response.data?.ok) setLeadAudienceCount(response.data.count);
      })
      .catch(() => {
        if (!cancelled) setLeadAudienceCount(null);
      })
      .finally(() => {
        if (!cancelled) setLeadAudienceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreateCampaign, isLeadCampaign, campaignForm.category, leadAudienceOpenedFilter, leadAudienceClickedFilter]);

  const handleSendLeadCampaign = async () => {
    if (!window.confirm(`Resend the cold email to all ${leadAudienceCount ?? 0} matching lead(s)?`)) {
      return;
    }
    setCampaignSending(true);
    // Sent in server-side batches — pass back nextAfterId until none remain.
    let sent = 0;
    let failed = 0;
    let total = 0;
    try {
      let afterId: string | null = null;
      for (;;) {
        const response: any = await axios.post(`${API_BASE_URL}/crm/leads/bulk-send-cold-email`, {
          category: campaignForm.category,
          openedFilter: leadAudienceOpenedFilter,
          clickedFilter: leadAudienceClickedFilter,
          afterId
        });
        if (!response.data?.ok) {
          setNewsletterError(response.data?.message || 'Could not send to leads.');
          break;
        }
        const batch = response.data;
        if (afterId === null) total = batch.total;
        sent += batch.sent;
        failed += batch.failed || 0;
        setCampaignProgress({ sentSoFar: sent + failed, total });
        if (!batch.remaining || !batch.processed) {
          setNewsletterMessage(`Cold email sent to ${sent} of ${total} leads${failed ? ` (${failed} failed — check those leads' email addresses)` : ''}.`);
          setShowCreateCampaign(false);
          break;
        }
        afterId = batch.nextAfterId;
      }
    } catch (sendError: any) {
      setNewsletterError(
        `${sendError?.response?.data?.message || 'Could not send to leads.'}${sent ? ` ${sent} lead(s) were sent before it stopped.` : ''}`
      );
    } finally {
      setCampaignSending(false);
      setCampaignProgress(null);
    }
  };

  // Campaigns send in server-side batches (each request fits inside the
  // hosting time limit) — keep asking for the next batch until everyone's
  // been sent to, or a batch makes no progress (e.g. delivery errors).
  type CampaignBatch = { sent: number; failed: number; sentSoFar: number; total: number; remaining: number; campaign?: { _id: string } };
  const [campaignProgress, setCampaignProgress] = useState<{ sentSoFar: number; total: number } | null>(null);
  const [finishingCampaignId, setFinishingCampaignId] = useState<string | null>(null);

  const runRemainingCampaignBatches = async (campaignId: string, first: CampaignBatch, attachments?: unknown[]) => {
    let batch = first;
    let failed = first.failed || 0;
    setCampaignProgress({ sentSoFar: batch.sentSoFar, total: batch.total });
    while (batch.remaining > 0) {
      const response = await axios.post(`${API_BASE_URL}/newsletter/campaigns/${campaignId}/continue`, { attachments });
      if (!response.data?.ok) throw new Error(response.data?.message || 'Could not continue sending.');
      batch = response.data;
      failed += batch.failed || 0;
      setCampaignProgress({ sentSoFar: batch.sentSoFar, total: batch.total });
      if (!batch.sent) break; // nothing went through this round — stop instead of looping
    }
    return { ...batch, failed };
  };

  const handleFinishCampaign = async (campaignId: string) => {
    if (!window.confirm('Send this campaign to the subscribers who haven\'t received it yet? Anyone who already got it is skipped.')) return;
    setFinishingCampaignId(campaignId);
    setNewsletterError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/campaigns/${campaignId}/continue`, {});
      if (!response.data?.ok) throw new Error(response.data?.message || 'Could not continue sending.');
      const result = await runRemainingCampaignBatches(campaignId, response.data);
      setNewsletterMessage(
        `Campaign now sent to ${result.sentSoFar} of ${result.total} subscribers` +
          (result.remaining > 0 ? ` — ${result.remaining} couldn't be sent (check the email addresses), try again later.` : '.')
      );
      fetchOutreachAnalytics();
    } catch (finishError: any) {
      setNewsletterError(finishError?.response?.data?.message || finishError?.message || 'Could not finish sending the campaign.');
    } finally {
      setFinishingCampaignId(null);
      setCampaignProgress(null);
    }
  };

  const handleSendCampaign = async () => {
    if (isLeadCampaign) return handleSendLeadCampaign();

    const recipientLabel = campaignForm.recipientCategories.map((c) => NEWSLETTER_CATEGORY_LABELS[c]).join(', ');
    const scheduledAt = campaignForm.scheduledAt ? new Date(campaignForm.scheduledAt) : null;
    const confirmLabel = scheduledAt
      ? `Schedule this to send to ${campaignRecipientCount} ${campaignRecipientIds ? 'selected ' : ''}subscriber(s)${campaignRecipientIds ? '' : ` interested in ${recipientLabel}`} at ${scheduledAt.toLocaleString()}?`
      : `Send this to ${campaignRecipientCount} ${campaignRecipientIds ? 'selected ' : ''}subscriber(s)${campaignRecipientIds ? '' : ` interested in ${recipientLabel}`}?`;
    if (!window.confirm(confirmLabel)) {
      return;
    }
    setCampaignSending(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/campaigns`, {
        category: campaignForm.category,
        recipientCategories: campaignForm.recipientCategories,
        recipientIds: effectiveRecipientIds,
        templateKey: campaignForm.templateKey,
        subject: campaignForm.subject,
        bodyText: campaignForm.bodyText,
        ctaLabel: campaignForm.ctaLabel,
        ctaUrl: campaignForm.ctaUrl,
        imageUrl: campaignForm.imageUrl,
        attachments: campaignAttachments,
        ...(scheduledAt ? { scheduledAt: scheduledAt.toISOString() } : {})
      });
      if (response.data?.ok) {
        const result = await runRemainingCampaignBatches(response.data.campaign._id, response.data, campaignAttachments);
        const shortfall =
          result.remaining > 0
            ? ` ${result.remaining} couldn't be sent yet — use "Finish sending" in Newsletters to retry.`
            : '';
        setNewsletterMessage(
          (scheduledAt
            ? `Campaign scheduled for ${scheduledAt.toLocaleString()} — ${result.sentSoFar} of ${result.total} subscriber(s) queued.`
            : `Campaign sent to ${result.sentSoFar} of ${result.total} subscribers.`) + shortfall
        );
        setShowCreateCampaign(false);
        fetchOutreachAnalytics();
      } else {
        setNewsletterError(response.data?.message || 'Could not send campaign.');
      }
    } catch (sendError: any) {
      setNewsletterError(
        (sendError?.response?.data?.message || sendError?.message || 'Could not send campaign.') +
          ' Anything already sent stays sent — use "Finish sending" in Newsletters to send the rest.'
      );
      fetchOutreachAnalytics();
    } finally {
      setCampaignSending(false);
      setCampaignProgress(null);
    }
  };

  const handleStartEditSubscriber = (subscriber: Subscriber) => {
    setEditingSubscriberId(subscriber._id);
    setSubscriberEditForm({
      email: subscriber.email || '',
      name: subscriber.name || '',
      phone: subscriber.phone || '',
      businessName: subscriber.businessName || '',
      socialUrl: subscriber.socialUrl || '',
      beats: subscriber.beats || false,
      mixing: subscriber.mixing || false,
      loopsTemplates: subscriber.loopsTemplates || false,
      visuals: subscriber.visuals || false,
      web: subscriber.web || false,
      ads: subscriber.ads || false
    });
  };

  const handleCancelEditSubscriber = () => {
    setEditingSubscriberId(null);
    setSubscriberEditForm({ email: '', name: '', phone: '', businessName: '', socialUrl: '', ...emptySubscriberInterests });
  };

  const handleSaveSubscriber = async (subscriberId: string) => {
    try {
      await axios.put(`${API_BASE_URL}/newsletter/subscribers/${subscriberId}`, subscriberEditForm);
      setNewsletterMessage('Subscriber updated.');
      setEditingSubscriberId(null);
      fetchSubscribers();
    } catch (saveError) {
      console.error(saveError);
      setNewsletterError('Could not update the subscriber.');
    }
  };

  const handleDeleteSubscriber = async (subscriberId: string) => {
    const confirmDelete = window.confirm('Delete this newsletter subscriber?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/newsletter/subscribers/${subscriberId}`);
      setNewsletterMessage('Subscriber deleted.');
      fetchSubscribers();
    } catch (deleteError) {
      console.error(deleteError);
      setNewsletterError('Could not delete the subscriber.');
    }
  };

  const findSubscriberField = (row: Record<string, any>, candidates: string[]) => {
    const keys = Object.keys(row);
    for (const candidate of candidates) {
      const match = keys.find((k) => k.trim().toLowerCase() === candidate);
      if (match) return row[match];
    }
    return undefined;
  };

  const toBoolField = (value: any) => {
    if (typeof value === 'boolean') return value;
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'yes' || normalized === 'true' || normalized === '1' || normalized === 'x';
  };

  const handleImportSubscribersClick = () => subscriberFileInputRef.current?.click();

  const handleImportSubscribersFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSubscriberImportStatus('Reading file...');
    try {
      const buffer = await file.arrayBuffer();
      const book = XLSX.read(buffer, { type: 'array' });
      const sheet = book.Sheets[book.SheetNames[0]];
      const rawRows: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet);

      const parsed = rawRows
        .map((row) => ({
          email: String(findSubscriberField(row, ['email', 'email address']) ?? '').trim(),
          name: String(findSubscriberField(row, ['name', 'full name']) ?? '').trim(),
          phone: String(findSubscriberField(row, ['phone', 'phone number']) ?? '').trim(),
          socialUrl: String(findSubscriberField(row, ['instagram', 'social', 'social url', 'socialurl']) ?? '').trim(),
          beats: toBoolField(findSubscriberField(row, ['beats'])),
          mixing: toBoolField(findSubscriberField(row, ['mixing'])),
          loopsTemplates: toBoolField(findSubscriberField(row, ['loopstemplates', 'loops & templates', 'loops and templates', 'loops'])),
          visuals: toBoolField(findSubscriberField(row, ['visuals'])),
          web: toBoolField(findSubscriberField(row, ['web'])),
          ads: toBoolField(findSubscriberField(row, ['ads']))
        }))
        .filter((row) => row.email);

      if (parsed.length === 0) {
        setSubscriberImportStatus('No rows with an email column found.');
        return;
      }

      // Hand off to the popup so interests can be picked for the whole list.
      setSubscriberImportStatus('');
      setFileImportInterests({ ...emptySubscriberInterests });
      setPendingFileImport({ fileName: file.name, rows: parsed });
    } catch (importError) {
      console.error(importError);
      setSubscriberImportStatus('Could not read that file — check the format and try again.');
    } finally {
      event.target.value = '';
    }
  };

  // How many rows in the pending file are already subscribers (by email,
  // ignoring case) — they still get the selected interests added on import.
  const pendingFileExistingCount = useMemo(() => {
    if (!pendingFileImport) return 0;
    const known = new Set(subscribers.map((sub) => String(sub.email || '').trim().toLowerCase()));
    return pendingFileImport.rows.filter((row) => known.has(String(row.email || '').trim().toLowerCase())).length;
  }, [pendingFileImport, subscribers]);

  const confirmFileImport = async () => {
    if (!pendingFileImport) return;
    const rows = applyImportInterests(pendingFileImport.rows, fileImportInterests);
    setImportingSubscribers(true);
    setSubscriberImportStatus(`Importing ${rows.length} rows...`);
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/subscribers/import`, { subscribers: rows });
      if (response.data?.ok) {
        const { insertedCount = 0, updatedCount = 0, skippedCount = 0 } = response.data;
        setSubscriberImportStatus(
          `Imported ${insertedCount} new` +
            (updatedCount ? `, added new info to ${updatedCount} existing` : '') +
            `, skipped ${skippedCount} (duplicate with nothing new, or missing email).`
        );
        fetchSubscribers();
      } else {
        setSubscriberImportStatus(response.data?.message || 'Import failed.');
      }
    } catch (importError) {
      console.error(importError);
      setSubscriberImportStatus('Import failed — please try again.');
    } finally {
      setImportingSubscribers(false);
      setPendingFileImport(null);
    }
  };


  useEffect(() => {
    if (isAuthenticated) {
      fetchClients();
      fetchAgreements();
      fetchWebsiteClients();
      fetchSubscribers();
      fetchContactSubmissions();
      fetchMockupRequests();
      fetchOutreachAnalytics();
      fetchBlogPosts();
    }
  }, [isAuthenticated]);

  const filteredWebsiteClients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return websiteClients.filter((client) => {
      const matchesQuery =
        !q ||
        (client.name || '').toLowerCase().includes(q) ||
        (client.email || '').toLowerCase().includes(q) ||
        (client.businessType || '').toLowerCase().includes(q) ||
        (client.address || '').toLowerCase().includes(q);

      const matchesWebsite =
        websiteFilter === 'all' ||
        (websiteFilter === 'has' && Boolean(client.website)) ||
        (websiteFilter === 'none' && !client.website);

      const hasIg = /instagram/i.test(client.socialMediaLinks || '');
      const matchesIg =
        igFilter === 'all' ||
        (igFilter === 'has' && hasIg) ||
        (igFilter === 'none' && !hasIg);

      return matchesQuery && matchesWebsite && matchesIg;
    });
  }, [websiteClients, searchQuery, websiteFilter, igFilter]);

  const filteredAgreements = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return agreements.filter((agreement) => {
      const matchesQuery = !q || agreement.clientName.toLowerCase().includes(q) || agreement.clientEmail.toLowerCase().includes(q);
      const matchesPlan = planFilter === 'all' || agreement.planType === planFilter;
      return matchesQuery && matchesPlan;
    });
  }, [agreements, searchQuery, planFilter]);

  const filteredSubscribers = useMemo(() => {
    const q = subscriberSearchQuery.trim().toLowerCase();
    return subscribers.filter((subscriber) => {
      const matchesQuery =
        !q ||
        (subscriber.email || '').toLowerCase().includes(q) ||
        (subscriber.name || '').toLowerCase().includes(q) ||
        (subscriber.phone || '').toLowerCase().includes(q) ||
        (subscriber.businessName || '').toLowerCase().includes(q) ||
        (subscriber.socialUrl || '').toLowerCase().includes(q);
      // 'any' = no filter; 'every' = every interest option checked; 'none' =
      // no interest option checked; otherwise that single interest.
      const matchesInterest =
        subscriberInterestFilter === 'any' ||
        (subscriberInterestFilter === 'every'
          ? INTEREST_FIELDS.every((field) => subscriber[field.key])
          : subscriberInterestFilter === 'none'
          ? !INTEREST_FIELDS.some((field) => subscriber[field.key])
          : subscriber[subscriberInterestFilter as keyof Subscriber]);
      return matchesQuery && matchesInterest;
    });
  }, [subscribers, subscriberSearchQuery, subscriberInterestFilter]);

  const totalSubscriberPages = Math.max(1, Math.ceil(filteredSubscribers.length / subscriberPageSize));

  useEffect(() => {
    setSubscriberPage(1);
  }, [subscriberPageSize, subscriberSearchQuery, subscriberInterestFilter]);

  useEffect(() => {
    if (subscriberPage > totalSubscriberPages) setSubscriberPage(totalSubscriberPages);
  }, [subscriberPage, totalSubscriberPages]);

  const paginatedSubscribers = useMemo(
    () => filteredSubscribers.slice((subscriberPage - 1) * subscriberPageSize, subscriberPage * subscriberPageSize),
    [filteredSubscribers, subscriberPage, subscriberPageSize]
  );

  const auditSignups = useMemo(
    () =>
      subscribers
        .filter((s) => s.freeaudit)
        .sort((a, b) => new Date(b.auditRequestedAt || b.createdAt).getTime() - new Date(a.auditRequestedAt || a.createdAt).getTime()),
    [subscribers]
  );

  const totalAuditPages = Math.max(1, Math.ceil(auditSignups.length / auditPageSize));

  useEffect(() => {
    setAuditPage(1);
  }, [auditPageSize]);

  useEffect(() => {
    if (auditPage > totalAuditPages) setAuditPage(totalAuditPages);
  }, [auditPage, totalAuditPages]);

  const paginatedAuditSignups = useMemo(
    () => auditSignups.slice((auditPage - 1) * auditPageSize, auditPage * auditPageSize),
    [auditSignups, auditPage, auditPageSize]
  );

  const AUDIT_SCORE_VARIANT: Record<string, string> = { Good: 'success', 'Needs Work': 'warning', Poor: 'danger' };

  const handleLogin = (event: React.FormEvent) => {
    event.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password.');
      setPassword('');
    }
  };

  const handleDownloadAll = (clientId: string) => {
    window.open(`${API_BASE_URL}/onboarding/clients/${clientId}/download-all`, '_blank');
  };

  const handleDeleteAttachment = async (clientId: string, attachmentId: string) => {
    const confirmDelete = window.confirm('Delete this uploaded file?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/onboarding/clients/${clientId}/files/${attachmentId}`);
      setMessage('Attachment deleted.');
      fetchClients();
    } catch (deleteError) {
      console.error(deleteError);
      setError('Could not delete the attachment.');
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    const confirmDelete = window.confirm('Delete this client onboarding record and its files?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/onboarding/clients/${clientId}`);
      setMessage('Client record deleted.');
      fetchClients();
    } catch (deleteError) {
      console.error(deleteError);
      setError('Could not delete the client record.');
    }
  };

  const handleDownloadAgreement = (agreementId: string) => {
    window.open(`${API_BASE_URL}/agreements/${agreementId}/download`, '_blank');
  };

  const handleDeleteAgreement = async (agreementId: string) => {
    const confirmDelete = window.confirm('Delete this signed agreement?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/agreements/${agreementId}`);
      setMessage('Agreement deleted.');
      fetchAgreements();
    } catch (deleteError) {
      console.error(deleteError);
      setError('Could not delete the agreement.');
    }
  };

  const handleStartEditWebsiteClient = (client: WebsiteClient) => {
    setEditingWebsiteClientId(client._id);
    setWebsiteClientEditForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      socialMediaLinks: client.socialMediaLinks || '',
      businessType: client.businessType || '',
      website: client.website || '',
      hasExistingWebsite: client.hasExistingWebsite ?? true,
      paid: Boolean(client.paid)
    });
  };

  const [sendingOnboardingClientId, setSendingOnboardingClientId] = useState<string | null>(null);
  const handleSendClientOnboarding = async (client: WebsiteClient) => {
    const again = client.onboardingEmailSentAt ? ' They already received it once.' : '';
    if (!window.confirm(`Send the onboarding email to ${client.name || client.email}?${again}`)) return;
    setSendingOnboardingClientId(client._id);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/website-clients/${client._id}/send-onboarding`);
      if (response.data?.ok) {
        setMessage(`Onboarding email sent to ${client.name || client.email}.`);
        fetchWebsiteClients();
      } else {
        setError(response.data?.message || 'Could not send onboarding email.');
      }
    } catch (sendError: any) {
      console.error(sendError);
      setError(sendError?.response?.data?.message || 'Could not send onboarding email.');
    } finally {
      setSendingOnboardingClientId(null);
    }
  };

  const handleCancelEditWebsiteClient = () => {
    setEditingWebsiteClientId(null);
    setWebsiteClientEditForm(emptyWebsiteClientForm);
  };

  const handleSaveWebsiteClient = async (clientId: string) => {
    try {
      await axios.put(`${API_BASE_URL}/website-clients/${clientId}`, websiteClientEditForm);
      setMessage('Website client updated.');
      setEditingWebsiteClientId(null);
      fetchWebsiteClients();
    } catch (saveError) {
      console.error(saveError);
      setError('Could not update the website client.');
    }
  };

  const handleDeleteWebsiteClient = async (clientId: string) => {
    const confirmDelete = window.confirm('Delete this website client record?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/website-clients/${clientId}`);
      setMessage('Website client deleted.');
      fetchWebsiteClients();
    } catch (deleteError) {
      console.error(deleteError);
      setError('Could not delete the website client.');
    }
  };

  const toggleWebsiteClientSelected = (clientId: string) => {
    setSelectedWebsiteClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAllWebsiteClients = () => {
    if (selectedWebsiteClientIds.size === filteredWebsiteClients.length) {
      setSelectedWebsiteClientIds(new Set());
    } else {
      setSelectedWebsiteClientIds(new Set(filteredWebsiteClients.map((client) => client._id)));
    }
  };

  const handleSendWebsiteReview = async (clientIds: string[]) => {
    if (!clientIds.length) return;
    setSendingReview(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/website-clients/send-review`, { ids: clientIds });
      if (response.data?.ok) {
        setMessage(`Sent website review email to ${response.data.sentCount} of ${clientIds.length} client${clientIds.length === 1 ? '' : 's'}.`);
        setSelectedWebsiteClientIds(new Set());
        fetchWebsiteClients();
      } else {
        setError(response.data?.message || 'Could not send website review emails.');
      }
    } catch (sendError) {
      console.error(sendError);
      setError('Could not send website review emails.');
    } finally {
      setSendingReview(false);
    }
  };

  const handleSendMarketingEmail = async (clientIds: string[]) => {
    if (!clientIds.length) return;
    setSendingMarketingEmail(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/website-clients/send-marketing-email`, { ids: clientIds });
      if (response.data?.ok) {
        setMessage(`Sent marketing email to ${response.data.sentCount} of ${clientIds.length} client${clientIds.length === 1 ? '' : 's'}.`);
        setSelectedWebsiteClientIds(new Set());
        fetchWebsiteClients();
      } else {
        setError(response.data?.message || 'Could not send marketing emails.');
      }
    } catch (sendError) {
      console.error(sendError);
      setError('Could not send marketing emails.');
    } finally {
      setSendingMarketingEmail(false);
    }
  };

  const handleSendPaymentReminder = async (clientIds: string[]) => {
    if (!clientIds.length) return;
    setSendingPaymentReminder(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/website-clients/send-payment-reminder`, { ids: clientIds });
      if (response.data?.ok) {
        setMessage(`Sent payment reminder to ${response.data.sentCount} of ${clientIds.length} client${clientIds.length === 1 ? '' : 's'}.`);
        setSelectedWebsiteClientIds(new Set());
        fetchWebsiteClients();
      } else {
        setError(response.data?.message || 'Could not send payment reminder emails.');
      }
    } catch (sendError) {
      console.error(sendError);
      setError('Could not send payment reminder emails.');
    } finally {
      setSendingPaymentReminder(false);
    }
  };

  const handleSendSubscriptionReminder = async (clientIds: string[]) => {
    if (!clientIds.length) return;
    setSendingSubscriptionReminder(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/website-clients/send-subscription-reminder`, { ids: clientIds });
      if (response.data?.ok) {
        setMessage(`Sent subscription reminder to ${response.data.sentCount} of ${clientIds.length} client${clientIds.length === 1 ? '' : 's'}.`);
        setSelectedWebsiteClientIds(new Set());
        fetchWebsiteClients();
      } else {
        setError(response.data?.message || 'Could not send subscription reminder emails.');
      }
    } catch (sendError) {
      console.error(sendError);
      setError('Could not send subscription reminder emails.');
    } finally {
      setSendingSubscriptionReminder(false);
    }
  };

  const openCustomClientEmail = (clientIds: string[]) => {
    if (!clientIds.length) return;
    setCustomClientEmailTargetIds(clientIds);
    setCustomClientEmailSubject('');
    setCustomClientEmailBody('');
    setShowCustomClientEmail(true);
  };

  const handleSendCustomClientEmail = async () => {
    if (!customClientEmailSubject.trim() || !customClientEmailBody.trim()) {
      setError('Subject and message body are required.');
      return;
    }
    setSendingCustomEmail(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/website-clients/send-custom-email`, {
        ids: customClientEmailTargetIds,
        subject: customClientEmailSubject,
        bodyText: customClientEmailBody
      });
      if (response.data?.ok) {
        setMessage(`Sent custom email to ${response.data.sentCount} of ${customClientEmailTargetIds.length} client${customClientEmailTargetIds.length === 1 ? '' : 's'}.`);
        setSelectedWebsiteClientIds(new Set());
        setShowCustomClientEmail(false);
        fetchWebsiteClients();
      } else {
        setError(response.data?.message || 'Could not send the custom email.');
      }
    } catch (sendError) {
      console.error(sendError);
      setError('Could not send the custom email.');
    } finally {
      setSendingCustomEmail(false);
    }
  };

  const handleAddWebsiteClient = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newWebsiteClient.name || !newWebsiteClient.email) {
      setError('Name and email are required to add a website client.');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/website-clients`, newWebsiteClient);
      setMessage('Website client added.');
      setNewWebsiteClient(emptyWebsiteClientForm);
      setShowAddWebsiteClient(false);
      fetchWebsiteClients();
    } catch (addError) {
      console.error(addError);
      setError('Could not add the website client.');
    }
  };

  const handleCopySubscribers = async () => {
    const list = filteredSubscribers.map((subscriber) => subscriber.email).join('\n');
    try {
      await navigator.clipboard.writeText(list);
      setNewsletterMessage('Subscriber list copied to clipboard.');
    } catch (copyError) {
      console.error(copyError);
      setNewsletterError('Could not copy to clipboard.');
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportSubscribersCsv = () => {
    const header = ['Email', 'Beats', 'Mixing', 'Loops & Templates', 'Visuals', 'Web', 'Ads', 'Subscribed At'];
    const rows = filteredSubscribers.map((subscriber) => [
      subscriber.email,
      subscriber.beats ? 'Yes' : 'No',
      subscriber.mixing ? 'Yes' : 'No',
      subscriber.loopsTemplates ? 'Yes' : 'No',
      subscriber.visuals ? 'Yes' : 'No',
      subscriber.web ? 'Yes' : 'No',
      subscriber.ads ? 'Yes' : 'No',
      new Date(subscriber.createdAt).toLocaleString()
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadBlob(new Blob([csv], { type: 'text/csv' }), 'newsletter-subscribers.csv');
  };

  const handleExportSubscribersXlsx = () => {
    const rows = filteredSubscribers.map((subscriber) => ({
      Email: subscriber.email,
      Beats: subscriber.beats ? 'Yes' : 'No',
      Mixing: subscriber.mixing ? 'Yes' : 'No',
      'Loops & Templates': subscriber.loopsTemplates ? 'Yes' : 'No',
      Visuals: subscriber.visuals ? 'Yes' : 'No',
      Web: subscriber.web ? 'Yes' : 'No',
      Ads: subscriber.ads ? 'Yes' : 'No',
      'Subscribed At': new Date(subscriber.createdAt).toLocaleString()
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscribers');
    XLSX.writeFile(workbook, 'newsletter-subscribers.xlsx');
  };

  if (!isAuthenticated) {
    return (
      <Container style={{ paddingTop: '6rem', paddingBottom: '3rem', maxWidth: '500px' }}>
        <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b' }}>
          <Card.Body>
            <h1 style={{ color: '#68FF00', marginBottom: '0.75rem' }}>Admin</h1>
            <p style={{ color: '#d4d4d4', marginBottom: '1.25rem' }}>
              Enter the admin password to view onboarding submissions.
            </p>
            {error ? <Alert variant="danger">{error}</Alert> : null}
            <Form onSubmit={handleLogin}>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter password" />
              </Form.Group>
              <Button type="submit" variant="success">Unlock</Button>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '6rem', paddingBottom: '3rem', maxWidth: '1600px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <h1 style={{ color: '#68FF00', margin: 0 }}>Admin</h1>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Button variant="outline-success" onClick={() => navigate('/admin/leads')}>Leads →</Button>
        </div>
      </div>
      <p style={{ color: '#d4d4d4', marginBottom: '1.5rem' }}>
        Review onboarding responses, download uploaded brand assets, and remove files once you are done with them.
      </p>

      <SiteAnalyticsPanel />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>ONBOARDING FORMS</h2>
        <Button size="sm" variant="success" onClick={() => setShowAddWebsiteClient((prev) => !prev)}>
          {showAddWebsiteClient ? 'Cancel' : '+ Add Website Client'}
        </Button>
      </div>

      {loading ? <p>Loading clients...</p> : null}

      {!loading && clients.length === 0 ? <Alert variant="secondary">No onboarding forms yet.</Alert> : null}

      {clients.map((client) => (
        <Card key={client._id} style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '1.25rem' }}>
          <Card.Body>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <h4 style={{ marginBottom: '0.25rem' }}>{client.businessName || client.clientName}</h4>
                <p style={{ marginBottom: '0.25rem' }}>{client.clientName} • {client.email}</p>
                <small>{new Date(client.createdAt).toLocaleString()}</small>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Button size="sm" variant="outline-success" onClick={() => handleDownloadAll(client._id)}>
                  Download All Files
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteClient(client._id)}>
                  Delete Client
                </Button>
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h6>Answers</h6>
              <ListGroup variant="flush" style={{ background: 'transparent' }}>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Business Type:</strong> {client.businessType || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Location:</strong> {client.location || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Bio:</strong> {client.bio || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Services:</strong> {client.servicesOffered.join(', ') || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Audience:</strong> {client.audience || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Goals:</strong> {client.goals || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Offers:</strong> {client.offers || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Color Scheme:</strong> {client.colorScheme || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Domain Name:</strong> {client.domainName || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Domain Status:</strong> {client.domainStatus || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Domain Details:</strong> {client.domainDetails || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Page Names / Nav Structure:</strong> {client.pageNames || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Instagram:</strong> {client.socialInstagram || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>TikTok:</strong> {client.socialTiktok || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>YouTube:</strong> {client.socialYoutube || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Facebook:</strong> {client.socialFacebook || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>X / Twitter:</strong> {client.socialTwitter || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Other Social Links:</strong> {client.socialOther || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>References:</strong> {client.references || '—'}</ListGroup.Item>
                <ListGroup.Item style={{ background: 'transparent', color: 'white' }}><strong>Notes:</strong> {client.notes || '—'}</ListGroup.Item>
              </ListGroup>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h6>Brand Logo</h6>
              {client.logoAttachments.length === 0 ? (
                <p>No logo files uploaded.</p>
              ) : (
                <ListGroup>
                  {client.logoAttachments.map((attachment) => (
                    <ListGroup.Item key={attachment._id} style={{ background: '#1a1a1a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                      <div>
                        <strong>{attachment.originalName || attachment.filename}</strong>
                        <div><small>{attachment.mimeType} • {Math.round(attachment.size / 1024)} KB</small></div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Button size="sm" variant="outline-light" onClick={() => window.open(`${API_BASE_URL}/onboarding/clients/${client._id}/files/${attachment._id}`, '_blank')}>
                          Download
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAttachment(client._id, attachment._id)}>
                          Delete
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h6>Files</h6>
              {client.attachments.length === 0 ? (
                <p>No files uploaded.</p>
              ) : (
                <ListGroup>
                  {client.attachments.map((attachment) => (
                    <ListGroup.Item key={attachment._id} style={{ background: '#1a1a1a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                      <div>
                        <strong>{attachment.originalName || attachment.filename}</strong>
                        <div><small>{attachment.mimeType} • {Math.round(attachment.size / 1024)} KB</small></div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Button size="sm" variant="outline-light" onClick={() => window.open(`${API_BASE_URL}/onboarding/clients/${client._id}/files/${attachment._id}`, '_blank')}>
                          Download
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAttachment(client._id, attachment._id)}>
                          Delete
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Card.Body>
        </Card>
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', marginTop: '2.5rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>Website Clients</h2>
        <Button size="sm" variant="success" onClick={() => setShowAddWebsiteClient((prev) => !prev)}>
          {showAddWebsiteClient ? 'Cancel' : '+ Add Website Client'}
        </Button>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <Form.Control
          placeholder="Search website clients & agreements by name or email..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          style={{ flex: '1 1 320px', minWidth: 0 }}
        />
        <Form.Select value={websiteFilter} onChange={(e) => setWebsiteFilter(e.target.value as 'all' | 'has' | 'none')} style={{ maxWidth: '220px' }}>
          <option value="all">Website: All</option>
          <option value="has">Has Website</option>
          <option value="none">No Website</option>
        </Form.Select>
        <Form.Select value={igFilter} onChange={(e) => setIgFilter(e.target.value as 'all' | 'has' | 'none')} style={{ maxWidth: '220px' }}>
          <option value="all">Instagram: All</option>
          <option value="has">Has Instagram</option>
          <option value="none">No Instagram</option>
        </Form.Select>
      </div>

      {showAddWebsiteClient ? (
        <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '1.25rem' }}>
          <Card.Body>
            <Form onSubmit={handleAddWebsiteClient}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control value={newWebsiteClient.name} onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, name: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={newWebsiteClient.email} onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, email: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control value={newWebsiteClient.phone} onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, phone: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control value={newWebsiteClient.address} onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, address: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Industry</Form.Label>
                <Form.Control
                  value={newWebsiteClient.businessType}
                  onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, businessType: e.target.value })}
                  placeholder="Select or type a new industry"
                  list="admin-industry-options"
                />
                <datalist id="admin-industry-options">
                  {INDUSTRY_OPTIONS.map((option) => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Social Media Links</Form.Label>
                <Form.Control value={newWebsiteClient.socialMediaLinks} onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, socialMediaLinks: e.target.value })} placeholder="Instagram, TikTok, etc." />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control value={newWebsiteClient.website} onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, website: e.target.value })} placeholder="https://..." />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Client already had this website (we didn't build it)"
                  checked={newWebsiteClient.hasExistingWebsite}
                  onChange={(e) => setNewWebsiteClient({ ...newWebsiteClient, hasExistingWebsite: e.target.checked })}
                />
              </Form.Group>
              <Button type="submit" variant="success">Save Client</Button>
            </Form>
          </Card.Body>
        </Card>
      ) : null}

      {loadingWebsiteClients ? <p>Loading website clients...</p> : null}

      {!loadingWebsiteClients && filteredWebsiteClients.length === 0 ? <Alert variant="secondary">No website clients match.</Alert> : null}

      {!loadingWebsiteClients && filteredWebsiteClients.length > 0 ? (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem', alignItems: 'center' }}>
          <Button size="sm" variant="outline-light" onClick={toggleSelectAllWebsiteClients}>
            {selectedWebsiteClientIds.size === filteredWebsiteClients.length ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            size="sm"
            variant="outline-success"
            disabled={selectedWebsiteClientIds.size === 0 || sendingReview}
            onClick={() => handleSendWebsiteReview(Array.from(selectedWebsiteClientIds))}
          >
            {sendingReview ? 'Sending...' : `Send Website Review${selectedWebsiteClientIds.size > 0 ? ` (${selectedWebsiteClientIds.size} selected)` : ''}`}
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={selectedWebsiteClientIds.size === 0 || sendingMarketingEmail}
            onClick={() => handleSendMarketingEmail(Array.from(selectedWebsiteClientIds))}
          >
            {sendingMarketingEmail ? 'Sending...' : `Send Marketing Email${selectedWebsiteClientIds.size > 0 ? ` (${selectedWebsiteClientIds.size} selected)` : ''}`}
          </Button>
          {/* Payment / subscription reminder buttons are hidden for now — the
              handlers and /api/website-clients/send-*-reminder routes are kept
              for when reminders come back (e.g. driven by Stripe status). */}
          <Button
            size="sm"
            variant="outline-light"
            disabled={selectedWebsiteClientIds.size === 0}
            onClick={() => openCustomClientEmail(Array.from(selectedWebsiteClientIds))}
          >
            {`Send Custom Email${selectedWebsiteClientIds.size > 0 ? ` (${selectedWebsiteClientIds.size} selected)` : ''}`}
          </Button>
        </div>
      ) : null}

      {filteredWebsiteClients.map((client) => {
        const isEditing = editingWebsiteClientId === client._id;

        if (isEditing) {
          return (
            <Card key={client._id} style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '0.75rem' }}>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control value={websiteClientEditForm.name} onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, name: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={websiteClientEditForm.email} onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, email: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control value={websiteClientEditForm.phone} onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, phone: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control value={websiteClientEditForm.address} onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, address: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Industry</Form.Label>
                  <Form.Control
                    value={websiteClientEditForm.businessType}
                    onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, businessType: e.target.value })}
                    placeholder="Select or type a new industry"
                    list="admin-industry-options"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Social Media Links</Form.Label>
                  <Form.Control value={websiteClientEditForm.socialMediaLinks} onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, socialMediaLinks: e.target.value })} placeholder="Instagram, TikTok, etc." />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control value={websiteClientEditForm.website} onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, website: e.target.value })} placeholder="https://..." />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Client already had this website (we didn't build it)"
                    checked={websiteClientEditForm.hasExistingWebsite}
                    onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, hasExistingWebsite: e.target.checked })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id={`client-paid-${client._id}`}
                    label="Client paid (hides Send Onboarding Email)"
                    checked={websiteClientEditForm.paid}
                    onChange={(e) => setWebsiteClientEditForm({ ...websiteClientEditForm, paid: e.target.checked })}
                  />
                </Form.Group>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button size="sm" variant="success" onClick={() => handleSaveWebsiteClient(client._id)}>Save</Button>
                  <Button size="sm" variant="outline-light" onClick={handleCancelEditWebsiteClient}>Cancel</Button>
                </div>
              </Card.Body>
            </Card>
          );
        }

        return (
          <div
            key={client._id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: '#111',
              border: '1px solid #2b2b2b',
              borderRadius: '8px',
              marginBottom: '0.5rem',
              flexWrap: 'wrap'
            }}
          >
            <Form.Check
              type="checkbox"
              checked={selectedWebsiteClientIds.has(client._id)}
              onChange={() => toggleWebsiteClientSelected(client._id)}
              style={{ flexShrink: 0 }}
            />
            {client.logo?.mimeType ? (
              <img
                src={`${API_BASE_URL}/website-clients/${client._id}/logo`}
                alt=""
                style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1a1a1a', color: '#68FF00', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                {(client.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: '2 1 200px', minWidth: 0 }}>
              <strong style={{ display: 'block' }}>
                {client.name || '—'}
                {client.paid ? (
                  <Badge bg="success" className="ms-2" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>Client</Badge>
                ) : client.leadStatus ? (
                  <Badge bg={LEAD_STATUS_BADGES[client.leadStatus].bg} className="ms-2" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                    {LEAD_STATUS_BADGES[client.leadStatus].label}
                  </Badge>
                ) : null}
              </strong>
              <small style={{ color: '#aaa' }}>{client.email}</small>
            </div>
            <div style={{ flex: '1 1 130px', fontSize: '0.85rem', color: '#ccc' }}>{client.businessType || '—'}</div>
            <div style={{ flex: '1 1 160px', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {client.website ? (
                <>
                  <a href={externalUrl(client.website)} target="_blank" rel="noreferrer" className="text-white">{client.website}</a>
                  {client.hasExistingWebsite ? (
                    <div><small style={{ color: '#888' }}>Existing website</small></div>
                  ) : null}
                </>
              ) : (
                <span style={{ color: '#ccc' }}>—</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
              {client.websiteReviewSentAt ? <Badge bg="success">Review Sent</Badge> : null}
              {client.marketingEmailSentAt ? <Badge bg="primary">Marketing Sent</Badge> : null}
              {client.paid ? <Badge bg="success">Onboarded</Badge> : null}
              {client.onboardingEmailSentAt ? <Badge bg="info" text="dark">Onboarding Sent</Badge> : null}
              {client.paymentReminderSentAt ? <Badge bg="warning" text="dark">Payment Reminder Sent</Badge> : null}
              {client.subscriptionReminderSentAt ? <Badge bg="warning" text="dark">Subscription Reminder Sent</Badge> : null}
              {client.lastCustomEmailSentAt ? <Badge bg="secondary">Custom Email Sent</Badge> : null}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap' }}>
              {!client.paid ? (
                <Button
                  size="sm"
                  variant="success"
                  disabled={sendingOnboardingClientId === client._id}
                  onClick={() => handleSendClientOnboarding(client)}
                >
                  {sendingOnboardingClientId === client._id
                    ? 'Sending...'
                    : client.onboardingEmailSentAt
                    ? 'Resend Onboarding Email'
                    : 'Send Onboarding Email'}
                </Button>
              ) : null}
              {!client.hasExistingWebsite ? (
                <Button size="sm" variant="outline-success" disabled={sendingReview} onClick={() => handleSendWebsiteReview([client._id])}>
                  Send Website Review
                </Button>
              ) : null}
              <Button size="sm" variant="outline-primary" disabled={sendingMarketingEmail} onClick={() => handleSendMarketingEmail([client._id])}>
                Send Marketing Email
              </Button>
              <Button size="sm" variant="outline-light" onClick={() => openCustomClientEmail([client._id])}>
                Custom Email
              </Button>
              <Button size="sm" variant="outline-light" onClick={() => handleStartEditWebsiteClient(client)}>Edit</Button>
              <Button size="sm" variant="outline-danger" onClick={() => handleDeleteWebsiteClient(client._id)}>Delete</Button>
            </div>
          </div>
        );
      })}

      {message ? <Alert variant="success" className="mt-3">{message}</Alert> : null}
      {error ? <Alert variant="danger" className="mt-3">{error}</Alert> : null}

      {/* Free Mockup Requests (left) and Free Audit Requests (right), side by side —
          stacked on smaller screens; Contact Us Submissions full width below. */}
      <Row className="g-4">
        <Col lg={6} style={{ minWidth: 0, overflowX: 'auto' }}>
          <div style={{ marginTop: '2.5rem', marginBottom: '0.5rem' }}>
            <h2 style={{ color: '#68FF00', margin: 0 }}>Free Mockup Requests</h2>
          </div>
          <p style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Inbound leads from the free-mockup request form, routed straight into the leads CRM.
          </p>

          {!loadingMockupRequests && mockupRequests.length === 0 ? <Alert variant="secondary">No mockup requests yet.</Alert> : null}

          {mockupRequests.length > 0 ? (
            <Table striped bordered hover variant="dark" responsive>
              <thead>
                <tr>
                  <th>Business / Contact</th>
                  <th>Contact Info</th>
                  <th>Channels</th>
                  <th>Requested</th>
                </tr>
              </thead>
              <tbody>
                {mockupRequests.map((lead) => {
                  const channelLinks: { label: string; value?: string }[] = [
                    { label: 'IG', value: lead.instagram },
                    { label: 'GBP', value: lead.googleBusinessUrl }
                  ];
                  return (
                    <tr key={lead._id}>
                      <td>
                        <strong>{lead.businessName || lead.contactName || lead.email}</strong>
                        {lead.businessName && lead.contactName ? <div><small style={{ color: '#aaa' }}>{lead.contactName}</small></div> : null}
                      </td>
                      <td>
                        <div>{lead.email || '—'}</div>
                        {lead.phone ? <small style={{ color: '#aaa' }}>{lead.phone}</small> : null}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                          {channelLinks.map((channel) => {
                            const badge = (
                              <Badge key={channel.label} bg={channel.value ? 'secondary' : 'dark'} style={{ opacity: channel.value ? 1 : 0.4 }}>
                                {channel.label}
                              </Badge>
                            );
                            if (!channel.value) return badge;
                            const href =
                              channel.label === 'IG'
                                ? socialUrl(channel.value, 'https://instagram.com')
                                : channel.label === 'FB'
                                ? socialUrl(channel.value, 'https://facebook.com')
                                : externalUrl(channel.value);
                            return (
                              <a key={channel.label} href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                {badge}
                              </a>
                            );
                          })}
                        </div>
                      </td>
                      <td><small>{new Date(lead.createdAt).toLocaleString()}</small></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : null}
        </Col>
        <Col lg={6} style={{ minWidth: 0, overflowX: 'auto' }}>
          <div style={{ marginTop: '2.5rem', marginBottom: '0.5rem' }}>
            <h2 style={{ color: '#68FF00', margin: 0 }}>Free Audit Requests</h2>
          </div>
          <p style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Automated online-presence review (website, Instagram, Facebook, Google Business). Rule-based, not a human read yet — use "Re-run" if a link failed to load.
          </p>

          {!loadingSubscribers && auditSignups.length === 0 ? <Alert variant="secondary">No audit requests yet.</Alert> : null}

          {auditSignups.length > 0 ? (
            <>
              <Row className="mb-2 align-items-center">
                <Col style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  Showing {(auditPage - 1) * auditPageSize + 1}–{Math.min(auditPage * auditPageSize, auditSignups.length)} of {auditSignups.length}
                </Col>
                <Col xs="auto">
                  <Form.Select
                    size="sm"
                    value={auditPageSize}
                    onChange={(event) => setAuditPageSize(Number(event.target.value))}
                    style={{ width: 'auto' }}
                  >
                    {AUDIT_PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size} per page</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              <Table striped bordered hover variant="dark" responsive>
                <thead>
                  <tr>
                    <th>Lead</th>
                    <th>Channels</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Summary</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAuditSignups.map((subscriber) => {
                    const isRunning = runningAuditId === subscriber._id;
                    const isExpanded = expandedAuditId === subscriber._id;
                    const channelLinks: { label: string; value?: string }[] = [
                      { label: 'Website', value: subscriber.website || subscriber.projectUrl },
                      { label: 'IG', value: subscriber.instagram },
                      { label: 'FB', value: subscriber.facebook },
                      { label: 'GBP', value: subscriber.googleBusinessUrl }
                    ];
                    return (
                      <Fragment key={subscriber._id}>
                        <tr>
                          <td>
                            <strong>{subscriber.name || subscriber.businessName || subscriber.email}</strong>
                            <br />
                            <small style={{ color: '#aaa' }}>{subscriber.email}</small>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                              {channelLinks.map((channel) => {
                                const badge = (
                                  <Badge key={channel.label} bg={channel.value ? 'secondary' : 'dark'} style={{ opacity: channel.value ? 1 : 0.4 }}>
                                    {channel.label}
                                  </Badge>
                                );
                                if (!channel.value) return badge;
                                const href =
                              channel.label === 'IG'
                                ? socialUrl(channel.value, 'https://instagram.com')
                                : channel.label === 'FB'
                                ? socialUrl(channel.value, 'https://facebook.com')
                                : externalUrl(channel.value);
                                return (
                                  <a key={channel.label} href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                    {badge}
                                  </a>
                                );
                              })}
                            </div>
                          </td>
                          <td>
                            {subscriber.auditScore ? (
                              <Badge bg={AUDIT_SCORE_VARIANT[subscriber.auditScore] || 'secondary'}>{subscriber.auditScore}</Badge>
                            ) : '—'}
                          </td>
                          <td>
                            {subscriber.auditStatus === 'pending' ? <Badge bg="info">Pending...</Badge> : null}
                            {subscriber.auditStatus === 'complete' ? <Badge bg="success">Complete</Badge> : null}
                            {subscriber.auditStatus === 'failed' ? <Badge bg="danger">Failed</Badge> : null}
                            {!subscriber.auditStatus ? '—' : null}
                          </td>
                          <td style={{ maxWidth: '360px' }}>
                            <small>{subscriber.auditSummary ? `${subscriber.auditSummary.slice(0, 120)}${subscriber.auditSummary.length > 120 ? '...' : ''}` : '—'}</small>
                          </td>
                          <td><small>{new Date(subscriber.auditRequestedAt || subscriber.createdAt).toLocaleString()}</small></td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <Button size="sm" variant="outline-light" onClick={() => setExpandedAuditId(isExpanded ? null : subscriber._id)}>
                                {isExpanded ? 'Hide' : 'Details'}
                              </Button>
                              <Button size="sm" variant="outline-info" disabled={isRunning} onClick={() => handleRunAudit(subscriber._id)}>
                                {isRunning ? 'Running...' : 'Re-run'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={7} style={{ background: '#0a0a0a' }}>
                              <div style={{ padding: '1rem' }}>
                                <p style={{ marginBottom: '0.75rem' }}>{subscriber.auditSummary || 'No summary yet.'}</p>
                                {subscriber.auditFindings && subscriber.auditFindings.length > 0 ? (
                                  <ListGroup variant="flush">
                                    {subscriber.auditFindings.map((finding, index) => (
                                      <ListGroup.Item key={index} style={{ background: 'transparent', color: '#d4d4d4', border: '1px solid #2b2b2b', fontSize: '0.85rem' }}>
                                        {finding}
                                      </ListGroup.Item>
                                    ))}
                                  </ListGroup>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>

              {totalAuditPages > 1 ? (
                <Pagination className="justify-content-center mt-3">
                  <Pagination.First onClick={() => setAuditPage(1)} disabled={auditPage === 1} />
                  <Pagination.Prev onClick={() => setAuditPage((page) => Math.max(1, page - 1))} disabled={auditPage === 1} />
                  {Array.from({ length: totalAuditPages }, (_, i) => i + 1).map((page) => (
                    <Pagination.Item key={page} active={page === auditPage} onClick={() => setAuditPage(page)}>
                      {page}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => setAuditPage((page) => Math.min(totalAuditPages, page + 1))} disabled={auditPage === totalAuditPages} />
                  <Pagination.Last onClick={() => setAuditPage(totalAuditPages)} disabled={auditPage === totalAuditPages} />
                </Pagination>
              ) : null}
            </>
          ) : null}
        </Col>
      </Row>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '2.5rem', marginBottom: '1rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>Signed Web Development Agreements</h2>
        <Form.Select value={planFilter} onChange={(event) => setPlanFilter(event.target.value as 'all' | Agreement['planType'])} style={{ maxWidth: '220px' }}>
          <option value="all">All Plan Types</option>
          <option value="one_time">One-Time Payment</option>
          <option value="monthly">Monthly Subscription</option>
          <option value="custom">Custom / Negotiated</option>
        </Form.Select>
      </div>

      {loadingAgreements ? <p>Loading agreements...</p> : null}

      {!loadingAgreements && filteredAgreements.length === 0 ? <Alert variant="secondary">No agreements match.</Alert> : null}

      {filteredAgreements.map((agreement) => (
        <div
          key={agreement._id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: '#111',
            border: '1px solid #2b2b2b',
            borderRadius: '8px',
            marginBottom: '0.5rem',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ flex: '2 1 200px', minWidth: 0 }}>
            <strong style={{ display: 'block' }}>{agreement.clientName}</strong>
            <small style={{ color: '#aaa' }}>{agreement.clientEmail}</small>
          </div>
          <div style={{ flex: '1 1 150px', fontSize: '0.85rem', color: '#ccc' }}>{PLAN_LABELS[agreement.planType]}</div>
          <div style={{ flex: '1 1 100px', fontSize: '0.85rem', color: '#ccc' }}>
            ${agreement.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ flex: '1 1 130px', fontSize: '0.8rem', color: '#888' }}>
            {new Date(agreement.effectiveDate).toLocaleDateString()}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
            <Button size="sm" variant="outline-success" onClick={() => handleDownloadAgreement(agreement._id)}>PDF</Button>
            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAgreement(agreement._id)}>Delete</Button>
          </div>
        </div>
      ))}

      <div style={{ marginTop: '2.5rem', marginBottom: '1rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>Contact Us Submissions</h2>
        <p style={{ color: '#d4d4d4', marginTop: '0.4rem', marginBottom: 0 }}>
          Messages sent through the "Contact Us" form on the About page. Submitters are also added to the newsletter separately.
        </p>
      </div>

      {!loadingContactSubmissions && contactSubmissions.length === 0 ? <Alert variant="secondary">No contact form submissions yet.</Alert> : null}

      {contactSubmissions.length > 0 ? (
        <Table striped bordered hover variant="dark" responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Interested In</th>
              <th>Message</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {contactSubmissions.map((submission) => {
              const interests = [
                submission.beats ? 'Music' : null,
                submission.visuals ? 'Branding' : null,
                submission.web ? 'Web Development' : null,
                submission.ads ? 'Ads' : null
              ].filter((value): value is string => Boolean(value));
              return (
                <tr key={submission._id}>
                  <td>{submission.name || '—'}</td>
                  <td>
                    <div>{submission.email}</div>
                    {submission.phone ? <small style={{ color: '#aaa' }}>{submission.phone}</small> : null}
                    {submission.socialUrl ? <div><small style={{ color: '#aaa' }}>{submission.socialUrl}</small></div> : null}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {interests.length > 0 ? interests.map((label) => <Badge key={label} bg="secondary">{label}</Badge>) : '—'}
                    </div>
                  </td>
                  <td style={{ maxWidth: '320px' }}>
                    <small>{submission.message ? submission.message : '—'}</small>
                  </td>
                  <td><small>{new Date(submission.createdAt).toLocaleString()}</small></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '2.5rem', marginBottom: '1rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>Subscribers</h2>
      </div>

      {newsletterMessage ? <Alert variant="success" onClose={() => setNewsletterMessage('')} dismissible>{newsletterMessage}</Alert> : null}
      {newsletterError ? <Alert variant="danger" onClose={() => setNewsletterError('')} dismissible>{newsletterError}</Alert> : null}

      <p style={{ color: '#d4d4d4', marginBottom: '1rem' }}>
        {subscribers.length} newsletter subscriber{subscribers.length === 1 ? '' : 's'}.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <Button size="sm" variant="outline-light" onClick={handleCopySubscribers} disabled={!filteredSubscribers.length}>
          Copy List
        </Button>
        <Button size="sm" variant="outline-success" onClick={handleExportSubscribersCsv} disabled={!filteredSubscribers.length}>
          Export CSV
        </Button>
        <Button size="sm" variant="outline-success" onClick={handleExportSubscribersXlsx} disabled={!filteredSubscribers.length}>
          Export XLSX
        </Button>
        <Button size="sm" variant="success" onClick={handleImportSubscribersClick}>
          Import CSV/XLSX
        </Button>
        <input
          ref={subscriberFileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleImportSubscribersFile}
        />
        <Button size="sm" variant="outline-success" onClick={() => setShowPasteSubscribers((prev) => !prev)}>
          {showPasteSubscribers ? 'Cancel' : '+ Paste From Excel'}
        </Button>
        <Button size="sm" variant="success" onClick={() => setShowAddSubscriber((prev) => !prev)}>
          {showAddSubscriber ? 'Cancel' : '+ Add Subscriber'}
        </Button>
      </div>

      {showPasteSubscribers ? (
        <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '1.25rem' }}>
          <Card.Body>
            <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
              Paste rows copied from Excel/Sheets — one subscriber per line, tab or comma separated. A header row
              (Email, Name, Phone, Business, Instagram) is used to match columns if present; otherwise that order is
              assumed. Any stray row that's just a "Genre" marker/divider is ignored automatically. Duplicate emails
              aren't added twice — any new info on them (name, phone, interests…) is saved to the existing subscriber.
            </p>
            <Form.Control
              as="textarea"
              rows={6}
              value={pasteSubscribersText}
              onChange={(e) => setPasteSubscribersText(e.target.value)}
              placeholder={'Email\tName\tPhone\tBusiness\tInstagram\njane@example.com\tJane Doe\t305-555-1234\tJane\'s Bakery\t@janesbakery'}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Interests for this list:</span>
                <ImportInterestPicker idPrefix="paste-interest" value={pasteInterests} onChange={setPasteInterests} />
              </div>
              <Button size="sm" variant="outline-light" onClick={handlePasteSubscribers} disabled={!pasteSubscribersText.trim() || importingSubscribers}>
                {importingSubscribers ? 'Importing...' : 'Detect & Import Subscribers'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {showAddSubscriber ? (
        <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '1.25rem' }}>
          <Card.Body>
            <Form onSubmit={handleAddSubscriber}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={newSubscriber.email} onChange={(e) => setNewSubscriber({ ...newSubscriber, email: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control value={newSubscriber.name} onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control value={newSubscriber.phone} onChange={(e) => setNewSubscriber({ ...newSubscriber, phone: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Business Name</Form.Label>
                <Form.Control value={newSubscriber.businessName} onChange={(e) => setNewSubscriber({ ...newSubscriber, businessName: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Instagram</Form.Label>
                <Form.Control value={newSubscriber.socialUrl} onChange={(e) => setNewSubscriber({ ...newSubscriber, socialUrl: e.target.value })} placeholder="@handle or profile URL" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Interests</Form.Label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {INTEREST_FIELDS.map((field) => (
                    <Form.Check
                      key={field.key}
                      type="checkbox"
                      label={field.label}
                      checked={newSubscriber[field.key]}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, [field.key]: e.target.checked })}
                    />
                  ))}
                </div>
              </Form.Group>
              <Button type="submit" variant="success">Save Subscriber</Button>
            </Form>
          </Card.Body>
        </Card>
      ) : null}

      {subscriberImportStatus ? <p style={{ color: '#d4d4d4', marginBottom: '1rem' }}>{subscriberImportStatus}</p> : null}

      {loadingSubscribers ? <p>Loading subscribers...</p> : null}

      {!loadingSubscribers && subscribers.length === 0 ? <Alert variant="secondary">No newsletter subscribers yet.</Alert> : null}

      {!loadingSubscribers && subscribers.length > 0 ? (
        <Row className="mb-2">
          <Col md={6}>
            <Form.Control
              size="sm"
              placeholder="Search by email, name, phone, business, Instagram..."
              value={subscriberSearchQuery}
              onChange={(event) => setSubscriberSearchQuery(event.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Select
              size="sm"
              value={subscriberInterestFilter}
              onChange={(event) => setSubscriberInterestFilter(event.target.value)}
            >
              <option value="any">Any Interests</option>
              {INTEREST_FIELDS.map((field) => (
                <option key={field.key} value={field.key}>{field.label}</option>
              ))}
              <option value="every">All Interests</option>
              <option value="none">None</option>
            </Form.Select>
          </Col>
        </Row>
      ) : null}

      {!loadingSubscribers && subscribers.length > 0 && filteredSubscribers.length === 0 ? (
        <Alert variant="secondary">No subscribers match your search/filter.</Alert>
      ) : null}

      {!loadingSubscribers && filteredSubscribers.length > 0 ? (
        <Row className="mb-2 align-items-center">
          <Col style={{ color: '#aaa', fontSize: '0.85rem' }}>
            Showing {(subscriberPage - 1) * subscriberPageSize + 1}–{Math.min(subscriberPage * subscriberPageSize, filteredSubscribers.length)} of {filteredSubscribers.length}
          </Col>
          <Col xs="auto">
            <Form.Select
              size="sm"
              value={subscriberPageSize}
              onChange={(event) => setSubscriberPageSize(Number(event.target.value))}
              style={{ width: 'auto' }}
            >
              {SUBSCRIBER_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      ) : null}

      {!loadingSubscribers && filteredSubscribers.length > 0 ? (
        <Table striped bordered hover variant="dark" responsive>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Business Name</th>
              <th>Instagram</th>
              <th>Interests</th>
              <th>Subscribed At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSubscribers.map((subscriber) => {
              const isEditing = editingSubscriberId === subscriber._id;
              if (isEditing) {
                return (
                  <tr key={subscriber._id}>
                    <td><Form.Control size="sm" type="email" value={subscriberEditForm.email} onChange={(e) => setSubscriberEditForm({ ...subscriberEditForm, email: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={subscriberEditForm.name} onChange={(e) => setSubscriberEditForm({ ...subscriberEditForm, name: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={subscriberEditForm.phone} onChange={(e) => setSubscriberEditForm({ ...subscriberEditForm, phone: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={subscriberEditForm.businessName} onChange={(e) => setSubscriberEditForm({ ...subscriberEditForm, businessName: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={subscriberEditForm.socialUrl} onChange={(e) => setSubscriberEditForm({ ...subscriberEditForm, socialUrl: e.target.value })} /></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {INTEREST_FIELDS.map((field) => (
                          <Form.Check
                            key={field.key}
                            type="checkbox"
                            label={field.label}
                            checked={subscriberEditForm[field.key]}
                            onChange={(e) => setSubscriberEditForm({ ...subscriberEditForm, [field.key]: e.target.checked })}
                          />
                        ))}
                      </div>
                    </td>
                    <td>{new Date(subscriber.createdAt).toLocaleString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <Button size="sm" variant="success" onClick={() => handleSaveSubscriber(subscriber._id)}>Save</Button>
                        <Button size="sm" variant="outline-light" onClick={handleCancelEditSubscriber}>Cancel</Button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <Fragment key={subscriber._id}>
                  <tr>
                    <td>{subscriber.email}</td>
                    <td>{subscriber.name || '—'}</td>
                    <td>{subscriber.phone || '—'}</td>
                    <td>{subscriber.businessName || '—'}</td>
                    <td>{subscriber.socialUrl || '—'}</td>
                    <td>{subscriberInterestLabel(subscriber)}</td>
                    <td>{new Date(subscriber.createdAt).toLocaleString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <Button size="sm" variant={contactOpenId === subscriber._id ? 'warning' : 'outline-warning'} onClick={() => handleOpenContact(subscriber)}>
                          {contactOpenId === subscriber._id ? 'Close' : 'Contact'}
                        </Button>
                        <Button size="sm" variant="outline-info" onClick={() => handleOpenSubscriberAnalytics(subscriber)}>Analytics</Button>
                        <Button size="sm" variant="outline-light" onClick={() => handleStartEditSubscriber(subscriber)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteSubscriber(subscriber._id)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                  {contactOpenId === subscriber._id ? (
                    <tr>
                      <td colSpan={8} style={{ background: '#0a0a0a' }}>
                        <div style={{ padding: '1rem' }}>
                          <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>
                            Contact {subscriber.name || subscriber.email}
                          </h4>
                          <Form.Group className="mb-2">
                            <Form.Label style={{ fontSize: '0.8rem' }}>Newsletter Type</Form.Label>
                            <Form.Select size="sm" style={{ maxWidth: '260px' }} value={contactForm.category} onChange={(e) => handleContactCategoryChange(e.target.value as NewsletterCategory)}>
                              {NEWSLETTER_CATEGORIES.map((cat) => (
                                <option key={cat} value={cat}>
                                  {NEWSLETTER_CATEGORY_LABELS[cat]}{subscriber[cat] ? ' ✓ subscribed' : ''}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>

                          <Form.Group className="mb-2">
                            <Form.Label style={{ fontSize: '0.8rem' }}>Resend a Previous Campaign (optional)</Form.Label>
                            <Form.Select size="sm" style={{ maxWidth: '360px' }} value={contactForm.resendCampaignId} onChange={(e) => handleContactResendCampaignChange(e.target.value)}>
                              <option value="">— Or compose a new email below —</option>
                              {contactCampaigns.map((c) => (
                                <option key={c._id} value={c._id}>{c.subject} ({new Date(c.createdAt).toLocaleDateString()})</option>
                              ))}
                            </Form.Select>
                          </Form.Group>

                          {!contactForm.resendCampaignId ? (
                            <>
                              <Form.Group className="mb-2">
                                <Form.Label style={{ fontSize: '0.8rem' }}>Template</Form.Label>
                                <Form.Select size="sm" style={{ maxWidth: '260px' }} value={contactForm.templateKey} onChange={(e) => handleContactTemplateChange(e.target.value)}>
                                  {Object.entries(TEMPLATE_PRESETS[contactForm.category]).map(([key, preset]) => (
                                    <option key={key} value={key}>{preset.label}</option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label style={{ fontSize: '0.8rem' }}>Subject</Form.Label>
                                <Form.Control size="sm" value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label style={{ fontSize: '0.8rem' }}>Message ("(name)" is replaced with their first name)</Form.Label>
                                <Form.Control as="textarea" rows={5} size="sm" value={contactForm.bodyText} onChange={(e) => setContactForm({ ...contactForm, bodyText: e.target.value })} />
                              </Form.Group>
                              <Form.Group className="mb-2">
                                <Form.Label style={{ fontSize: '0.8rem' }}>Image URL (optional — shown above the button)</Form.Label>
                                <Form.Control size="sm" value={contactForm.imageUrl} onChange={(e) => setContactForm({ ...contactForm, imageUrl: e.target.value })} placeholder="https://..." />
                                {contactForm.imageUrl ? (
                                  <img src={contactForm.imageUrl} alt="" style={{ maxWidth: '160px', marginTop: '0.5rem', borderRadius: '6px', display: 'block' }} />
                                ) : null}
                              </Form.Group>
                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label style={{ fontSize: '0.8rem' }}>Button Label (optional)</Form.Label>
                                    <Form.Control size="sm" value={contactForm.ctaLabel} onChange={(e) => setContactForm({ ...contactForm, ctaLabel: e.target.value })} />
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label style={{ fontSize: '0.8rem' }}>Button Link (optional)</Form.Label>
                                    <Form.Control size="sm" value={contactForm.ctaUrl} onChange={(e) => setContactForm({ ...contactForm, ctaUrl: e.target.value })} />
                                  </Form.Group>
                                </Col>
                              </Row>
                              <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: '0.8rem' }}>
                                  Attach Files {contactForm.category === 'beats' && contactForm.templateKey === 'custom-beats-for-you' ? '(upload the beats to send)' : '(optional)'}
                                </Form.Label>
                                <Form.Control size="sm" type="file" multiple onChange={(e) => handleContactAttachmentChange((e.target as HTMLInputElement).files)} />
                                {contactAttachments.length ? (
                                  <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.25rem' }}>
                                    {contactAttachments.map((a) => a.filename).join(', ')}
                                  </div>
                                ) : null}
                                {(contactForm.category === 'beats' || contactForm.category === 'loopsTemplates') ? (
                                  <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                                    The {contactForm.category === 'beats' ? 'Beats' : 'Loops & Templates'} Terms of Usage PDF is attached automatically.
                                  </div>
                                ) : null}
                              </Form.Group>
                            </>
                          ) : (
                            (() => {
                              const campaign = contactCampaigns.find((c) => c._id === contactForm.resendCampaignId);
                              if (!campaign) return null;
                              return (
                                <div style={{ background: '#0a0a0a', border: '1px solid #2b2b2b', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                                  <p style={{ fontSize: '0.7rem', color: '#666', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Preview — exact content that will be sent
                                  </p>
                                  <p style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{campaign.subject}</p>
                                  <p style={{ whiteSpace: 'pre-wrap', color: '#d4d4d4', fontSize: '0.85rem', marginBottom: campaign.imageUrl || campaign.ctaUrl ? '0.75rem' : 0 }}>
                                    {campaign.html}
                                  </p>
                                  {campaign.imageUrl ? (
                                    <img src={campaign.imageUrl} alt="" style={{ maxWidth: '100%', borderRadius: '8px', display: 'block', margin: '0 auto 0.75rem' }} />
                                  ) : null}
                                  {campaign.ctaUrl ? (
                                    <div style={{ textAlign: 'center' }}>
                                      <span style={{ background: '#68FF00', color: '#111', fontWeight: 700, padding: '8px 18px', borderRadius: '6px', fontSize: '0.8rem', display: 'inline-block' }}>
                                        {campaign.ctaLabel || 'Learn more'}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })()
                          )}

                          <Button size="sm" variant="success" disabled={contactSending || !contactForm.subject || !contactForm.bodyText} onClick={() => handleSendContact(subscriber)}>
                            {contactSending ? 'Sending...' : `Send to ${subscriber.email}`}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      ) : null}

      {!loadingSubscribers && totalSubscriberPages > 1 ? (
        <Pagination className="justify-content-center mt-3">
          <Pagination.First onClick={() => setSubscriberPage(1)} disabled={subscriberPage === 1} />
          <Pagination.Prev onClick={() => setSubscriberPage((page) => Math.max(1, page - 1))} disabled={subscriberPage === 1} />
          {Array.from({ length: totalSubscriberPages }, (_, index) => index + 1)
            .filter((page) => page === 1 || page === totalSubscriberPages || Math.abs(page - subscriberPage) <= 2)
            .map((page, index, pages) => (
              <span key={page} style={{ display: 'contents' }}>
                {index > 0 && pages[index - 1] !== page - 1 ? <Pagination.Ellipsis disabled /> : null}
                <Pagination.Item active={page === subscriberPage} onClick={() => setSubscriberPage(page)}>
                  {page}
                </Pagination.Item>
              </span>
            ))}
          <Pagination.Next onClick={() => setSubscriberPage((page) => Math.min(totalSubscriberPages, page + 1))} disabled={subscriberPage === totalSubscriberPages} />
          <Pagination.Last onClick={() => setSubscriberPage(totalSubscriberPages)} disabled={subscriberPage === totalSubscriberPages} />
        </Pagination>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '2.5rem', marginBottom: '1rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>Newsletters</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button size="sm" variant="outline-warning" onClick={handleOpenCreateCampaign}>
            + Create Campaign
          </Button>
          <Button size="sm" variant="outline-info" onClick={fetchOutreachAnalytics}>Refresh</Button>
        </div>
      </div>

      {loadingOutreachAnalytics ? <p>Loading outreach analytics...</p> : null}

      {!loadingOutreachAnalytics && outreachAnalytics ? (
        <>
          <Row className="g-3 mb-3">
            {[
              { label: 'Personal Emails', stats: outreachAnalytics.personal, hint: 'One-off sends from the Contact panel' },
              { label: 'Signup Thank-Yous', stats: outreachAnalytics.signup, hint: 'Automatic emails sent at signup' },
              { label: 'Campaigns', stats: outreachAnalytics.campaignsSummary, hint: `${outreachAnalytics.campaignsSummary.totalCampaigns} campaign(s) sent` }
            ].map((block) => (
              <Col md={4} key={block.label}>
                <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', height: '100%' }}>
                  <Card.Body>
                    <h5 style={{ fontSize: '0.95rem', color: '#aaa', marginBottom: '0.25rem' }}>{block.label}</h5>
                    <small style={{ color: '#666' }}>{block.hint}</small>
                    <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{block.stats.sent}</div>
                        <small style={{ color: '#888' }}>Sent</small>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#68FF00' }}>{(block.stats.openRate * 100).toFixed(0)}%</div>
                        <small style={{ color: '#888' }}>Open rate</small>
                      </div>
                      <div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{(block.stats.clickRate * 100).toFixed(0)}%</div>
                        <small style={{ color: '#888' }}>Click rate</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {outreachAnalytics.campaigns.length > 0 ? (
            <Table striped bordered hover variant="dark" responsive size="sm">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Sent</th>
                  <th>Opened</th>
                  <th>Clicked</th>
                  <th>Open Rate</th>
                  <th>Click Rate</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {outreachAnalytics.campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>{campaign.subject || '—'}</td>
                    <td>{campaign.category ? NEWSLETTER_CATEGORY_LABELS[campaign.category as NewsletterCategory] || campaign.category : '—'}</td>
                    <td>
                      {campaign.recipientCount && campaign.sent < campaign.recipientCount ? (
                        <>
                          {campaign.sent} of {campaign.recipientCount}
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="ms-2 py-0"
                            disabled={finishingCampaignId !== null}
                            onClick={() => handleFinishCampaign(campaign.id)}
                          >
                            {finishingCampaignId === campaign.id ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                {campaignProgress ? `${campaignProgress.sentSoFar} of ${campaignProgress.total}` : 'Sending'}
                              </>
                            ) : (
                              'Finish sending'
                            )}
                          </Button>
                        </>
                      ) : (
                        campaign.sent
                      )}
                    </td>
                    <td>{campaign.opened}</td>
                    <td>{campaign.clicked}</td>
                    <td>{(campaign.openRate * 100).toFixed(0)}%</td>
                    <td>{(campaign.clickRate * 100).toFixed(0)}%</td>
                    <td><small>{campaign.sentAt ? new Date(campaign.sentAt).toLocaleDateString() : '—'}</small></td>
                    <td>
                      <Button size="sm" variant="outline-light" className="py-0" onClick={() => setDetailsCampaignId(campaign.id)}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="secondary">No campaigns sent yet.</Alert>
          )}
        </>
      ) : null}

      <Modal show={Boolean(pendingFileImport)} onHide={() => !importingSubscribers && setPendingFileImport(null)} size="lg" centered>
        <Modal.Header style={{ background: '#111', color: 'white', borderBottom: '1px solid #2b2b2b' }}>
          <Modal.Title>Import {pendingFileImport?.rows.length ?? 0} subscribers</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#111', color: 'white' }}>
          <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
            Found <strong style={{ color: 'white' }}>{pendingFileImport?.rows.length ?? 0}</strong> rows with an email in{' '}
            <strong style={{ color: 'white' }}>{pendingFileImport?.fileName}</strong>. Pick the interests to apply to the
            whole list — they're added on top of any interest columns already in the file.
          </p>
          {pendingFileExistingCount > 0 ? (
            <Alert variant="info" style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
              {pendingFileExistingCount} of these {pendingFileExistingCount === 1 ? 'is' : 'are'} already subscribed. They
              won&apos;t be added twice, but they&apos;ll still get the interests you pick here plus any new info from
              the file (nothing they already have is overwritten).
            </Alert>
          ) : null}
          <Form.Label style={{ fontSize: '0.85rem' }}>Interests for this list</Form.Label>
          <ImportInterestPicker idPrefix="file-interest" value={fileImportInterests} onChange={setFileImportInterests} />
          <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: '1rem', border: '1px solid #2b2b2b', borderRadius: 6 }}>
            <Table size="sm" variant="dark" className="mb-0" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Phone</th>
                </tr>
              </thead>
              <tbody>
                {(pendingFileImport?.rows || []).slice(0, 50).map((row, idx) => (
                  <tr key={`${row.email}-${idx}`}>
                    <td>{row.email}</td>
                    <td>{row.name || '—'}</td>
                    <td>{row.phone || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {(pendingFileImport?.rows.length ?? 0) > 50 ? (
            <small style={{ color: '#888' }}>Showing the first 50 of {pendingFileImport?.rows.length}.</small>
          ) : null}
        </Modal.Body>
        <Modal.Footer style={{ background: '#111', borderTop: '1px solid #2b2b2b' }}>
          <Button variant="outline-light" onClick={() => setPendingFileImport(null)} disabled={importingSubscribers}>
            Cancel
          </Button>
          <Button variant="success" onClick={confirmFileImport} disabled={importingSubscribers}>
            {importingSubscribers ? 'Importing...' : `Import ${pendingFileImport?.rows.length ?? 0} Subscribers`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(analyticsSubscriber)} onHide={() => setAnalyticsSubscriber(null)} size="lg" centered>
        <Modal.Header style={{ background: '#111', color: 'white', borderBottom: '1px solid #2b2b2b' }}>
          <Modal.Title>
            Analytics — {analyticsSubscriber?.name || analyticsSubscriber?.email}
          </Modal.Title>
          <button type="button" className="btn-close btn-close-danger" aria-label="Close" onClick={() => setAnalyticsSubscriber(null)} />
        </Modal.Header>
        <Modal.Body style={{ background: '#111', color: 'white' }}>
          {loadingSubscriberAnalytics ? <p>Loading...</p> : null}
          {!loadingSubscriberAnalytics && analyticsSends.length === 0 ? (
            <Alert variant="secondary">No emails sent to this subscriber yet.</Alert>
          ) : null}
          {!loadingSubscriberAnalytics && analyticsSends.length > 0 ? (
            <Table striped bordered hover variant="dark" responsive size="sm">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Template</th>
                  <th>Subject</th>
                  <th>Sent</th>
                  <th>Opened</th>
                  <th>Clicked</th>
                </tr>
              </thead>
              <tbody>
                {analyticsSends.map((send) => (
                  <tr key={send._id}>
                    <td>
                      <Badge bg={send.category === 'signup' ? 'secondary' : 'info'}>
                        {send.category === 'signup' ? 'Signup' : NEWSLETTER_CATEGORY_LABELS[send.category as NewsletterCategory] || send.category}
                      </Badge>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: '#aaa' }}>{send.templateKey || '—'}</td>
                    <td>{send.subject}</td>
                    <td>{new Date(send.sentAt).toLocaleString()}</td>
                    <td>{send.opened ? <span style={{ color: '#68FF00' }}>✓ Opened</span> : <span style={{ color: '#666' }}>Not opened</span>}</td>
                    <td>{send.clicked ? <span style={{ color: '#38bdf8' }}>✓ Clicked</span> : <span style={{ color: '#666' }}>Not clicked</span>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Modal.Body>
        <Modal.Footer style={{ background: '#111', borderTop: '1px solid #2b2b2b' }}>
          <Button variant="outline-light" size="sm" onClick={() => setAnalyticsSubscriber(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <RecipientPickerModal
        show={showRecipientPicker}
        subscribers={subscribers}
        campaignInterests={campaignForm.recipientCategories}
        initialSelected={effectiveRecipientIds}
        onCancel={() => setShowRecipientPicker(false)}
        onDone={(ids) => {
          setCampaignRecipientIds(ids);
          setShowRecipientPicker(false);
        }}
      />
      <CampaignDetailsModal campaignId={detailsCampaignId} onClose={() => setDetailsCampaignId(null)} />

      <Modal show={showCreateCampaign && !showRecipientPicker} onHide={() => setShowCreateCampaign(false)} size="lg" centered>
        <Modal.Header style={{ background: '#111', color: 'white', borderBottom: '1px solid #2b2b2b' }}>
          <Modal.Title>Create Campaign</Modal.Title>
          <button type="button" className="btn-close btn-close-danger" aria-label="Close" onClick={() => setShowCreateCampaign(false)} />
        </Modal.Header>
        <Modal.Body style={{ background: '#111', color: 'white' }}>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.8rem' }}>Newsletter Type{isLeadCampaign ? '' : ' (picks the template)'}</Form.Label>
            <Form.Select size="sm" value={campaignForm.category} onChange={(e) => handleCampaignCategoryChange(e.target.value as NewsletterCategory)}>
              {NEWSLETTER_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{NEWSLETTER_CATEGORY_LABELS[cat]}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {isLeadCampaign ? (
            <>
              <p style={{ fontSize: '0.8rem', color: '#aaa' }}>
                {campaignForm.category === 'ads'
                  ? 'Ads campaigns always resend the marketing/ads cold-email pitch to leads that already have a website.'
                  : 'Web Development campaigns always resend the free-mockup cold-email pitch to leads without a website yet.'}{' '}
                Leads already onboarded (moved to Website Clients) or declined are excluded automatically.
              </p>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8rem' }}>Opened the cold email?</Form.Label>
                    <Form.Select size="sm" value={leadAudienceOpenedFilter} onChange={(e) => setLeadAudienceOpenedFilter(e.target.value as typeof leadAudienceOpenedFilter)}>
                      <option value="all">Doesn't matter</option>
                      <option value="not_opened">Hasn't opened</option>
                      <option value="opened">Has opened</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8rem' }}>Clicked the link?</Form.Label>
                    <Form.Select size="sm" value={leadAudienceClickedFilter} onChange={(e) => setLeadAudienceClickedFilter(e.target.value as typeof leadAudienceClickedFilter)}>
                      <option value="all">Doesn't matter</option>
                      <option value="not_clicked">Hasn't clicked</option>
                      <option value="clicked">Has clicked</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Alert variant="secondary" style={{ fontSize: '0.85rem' }}>
                {leadAudienceLoading ? 'Counting matching leads...' : (
                  <>This will resend the cold email to <strong>{leadAudienceCount ?? 0}</strong> matching lead{leadAudienceCount === 1 ? '' : 's'}.</>
                )}
              </Alert>
            </>
          ) : (
            <>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Send to subscribers interested in</Form.Label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {NEWSLETTER_CATEGORIES.filter((cat) => !LEAD_CAMPAIGN_CATEGORIES.includes(cat)).map((cat) => (
                    <Form.Check
                      key={cat}
                      type="checkbox"
                      label={NEWSLETTER_CATEGORY_LABELS[cat]}
                      checked={campaignForm.recipientCategories.includes(cat)}
                      onChange={() => toggleCampaignRecipientCategory(cat)}
                    />
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Template</Form.Label>
                <Form.Select size="sm" value={campaignForm.templateKey} onChange={(e) => handleCampaignTemplateChange(e.target.value)}>
                  {Object.entries(TEMPLATE_PRESETS[campaignForm.category]).map(([key, preset]) => (
                    <option key={key} value={key}>{preset.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Subject</Form.Label>
                <Form.Control size="sm" value={campaignForm.subject} onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Message ("(name)" is replaced with each recipient's first name)</Form.Label>
                <Form.Control as="textarea" rows={5} size="sm" value={campaignForm.bodyText} onChange={(e) => setCampaignForm({ ...campaignForm, bodyText: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Image URL (optional — shown above the button)</Form.Label>
                <Form.Control size="sm" value={campaignForm.imageUrl} onChange={(e) => setCampaignForm({ ...campaignForm, imageUrl: e.target.value })} placeholder="https://..." />
                {campaignForm.imageUrl ? (
                  <img src={campaignForm.imageUrl} alt="" style={{ maxWidth: '160px', marginTop: '0.5rem', borderRadius: '6px', display: 'block' }} />
                ) : null}
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8rem' }}>Button Label (optional)</Form.Label>
                    <Form.Control size="sm" value={campaignForm.ctaLabel} onChange={(e) => setCampaignForm({ ...campaignForm, ctaLabel: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8rem' }}>Button Link (optional)</Form.Label>
                    <Form.Control size="sm" value={campaignForm.ctaUrl} onChange={(e) => setCampaignForm({ ...campaignForm, ctaUrl: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Attach Files (optional — sent to every recipient)</Form.Label>
                <Form.Control size="sm" type="file" multiple onChange={(e) => handleCampaignAttachmentChange((e.target as HTMLInputElement).files)} />
                {campaignAttachments.length ? (
                  <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.25rem' }}>{campaignAttachments.map((a) => a.filename).join(', ')}</div>
                ) : null}
                {campaignForm.category === 'beats' || campaignForm.category === 'loopsTemplates' ? (
                  <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                    The {campaignForm.category === 'beats' ? 'Beats' : 'Loops & Templates'} Terms of Usage PDF is attached automatically.
                  </div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Schedule for later (optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={campaignForm.scheduledAt}
                  min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  onChange={(e) => setCampaignForm({ ...campaignForm, scheduledAt: e.target.value })}
                />
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                  Leave blank to send immediately. Otherwise Resend holds and delivers every
                  recipient's email at this time (your local time) instead of right away.
                </div>
              </Form.Group>
              <Alert variant="secondary" style={{ fontSize: '0.85rem' }}>
                {campaignRecipientIds ? (
                  <>This will send to <strong>{campaignRecipientCount}</strong> hand-picked subscriber{campaignRecipientCount === 1 ? '' : 's'}.</>
                ) : campaignForm.recipientCategories.length ? (
                  <>This will send to <strong>{campaignRecipientCount}</strong> subscriber{campaignRecipientCount === 1 ? '' : 's'} currently interested in {campaignForm.recipientCategories.map((c) => NEWSLETTER_CATEGORY_LABELS[c]).join(', ')}.</>
                ) : (
                  'Select at least one interest, or pick recipients by hand.'
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <Button size="sm" variant="outline-dark" onClick={() => setShowRecipientPicker(true)}>
                    View / edit recipients
                  </Button>
                  {campaignRecipientIds ? (
                    <Button size="sm" variant="link" className="p-0" onClick={() => setCampaignRecipientIds(null)}>
                      Reset to everyone matching the interests
                    </Button>
                  ) : null}
                </div>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#111', borderTop: '1px solid #2b2b2b' }}>
          {campaignSending ? (
            <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto', color: '#d4d4d4', fontSize: '0.85rem' }}>
              <Spinner animation="border" size="sm" variant="warning" />
              {campaignForm.scheduledAt && !isLeadCampaign ? 'Scheduling' : 'Sending'}
              {campaignProgress ? ` ${campaignProgress.sentSoFar} of ${campaignProgress.total}` : ''}… keep this window open
            </div>
          ) : null}
          <Button variant="outline-light" size="sm" onClick={() => setShowCreateCampaign(false)} disabled={campaignSending}>Cancel</Button>
          {isLeadCampaign ? (
            <Button variant="warning" size="sm" disabled={campaignSending || leadAudienceLoading || !leadAudienceCount} onClick={handleSendCampaign}>
              {campaignSending ? 'Sending...' : `Send Cold Email to ${leadAudienceCount ?? 0}`}
            </Button>
          ) : (
            <Button variant="warning" size="sm" disabled={campaignSending || !campaignForm.subject || !campaignForm.bodyText || !campaignRecipientCount} onClick={handleSendCampaign}>
              {campaignSending
                ? (campaignForm.scheduledAt ? 'Scheduling...' : 'Sending...')
                : campaignForm.scheduledAt
                ? `Schedule Campaign for ${campaignRecipientCount}`
                : `Send Campaign to ${campaignRecipientCount}`}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showCustomClientEmail} onHide={() => setShowCustomClientEmail(false)} size="lg" centered>
        <Modal.Header style={{ background: '#111', color: 'white', borderBottom: '1px solid #2b2b2b' }}>
          <Modal.Title>
            Custom Email {customClientEmailTargetIds.length > 0 ? `(${customClientEmailTargetIds.length} recipient${customClientEmailTargetIds.length === 1 ? '' : 's'})` : ''}
          </Modal.Title>
          <button type="button" className="btn-close btn-close-danger" aria-label="Close" onClick={() => setShowCustomClientEmail(false)} />
        </Modal.Header>
        <Modal.Body style={{ background: '#111', color: 'white' }}>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.8rem' }}>Subject</Form.Label>
            <Form.Control
              size="sm"
              value={customClientEmailSubject}
              onChange={(e) => setCustomClientEmailSubject(e.target.value)}
              placeholder="Subject"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.8rem' }}>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={customClientEmailBody}
              onChange={(e) => setCustomClientEmailBody(e.target.value)}
              placeholder="One paragraph per line..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ background: '#111', borderTop: '1px solid #2b2b2b' }}>
          <Button variant="outline-light" size="sm" onClick={() => setShowCustomClientEmail(false)} disabled={sendingCustomEmail}>Cancel</Button>
          <Button
            variant="warning"
            size="sm"
            disabled={sendingCustomEmail || !customClientEmailSubject.trim() || !customClientEmailBody.trim()}
            onClick={handleSendCustomClientEmail}
          >
            {sendingCustomEmail ? 'Sending...' : `Send to ${customClientEmailTargetIds.length}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <MarketingAnalyticsPanel />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginTop: '5rem', marginBottom: '1rem' }}>
        <h2 style={{ color: '#68FF00', margin: 0 }}>Blog</h2>
        <Button
          size="sm"
          variant="outline-success"
          onClick={() => {
            handleCancelEditBlogPost();
            setShowAddBlogPost((prev) => !prev);
          }}
        >
          {showAddBlogPost ? 'Cancel' : '+ New Post'}
        </Button>
      </div>
      <p style={{ color: '#d4d4d4', marginBottom: '1rem' }}>
        Posts published here show up on /Blog alongside the built-in posts, credited to @_enigmalabs.
      </p>

      {blogMessage ? <Alert variant="success" dismissible onClose={() => setBlogMessage('')}>{blogMessage}</Alert> : null}
      {blogError ? <Alert variant="danger" dismissible onClose={() => setBlogError('')}>{blogError}</Alert> : null}

      {showAddBlogPost ? (
        <Card style={{ background: '#111', color: 'white', border: '1px solid #2b2b2b', marginBottom: '1.5rem' }}>
          <Card.Body>
            <Form onSubmit={handleAddBlogPost}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8rem' }}>Title</Form.Label>
                    <Form.Control size="sm" value={newBlogPost.Title} onChange={(e) => setNewBlogPost({ ...newBlogPost, Title: e.target.value })} required />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: '0.8rem' }}>Category</Form.Label>
                    <Form.Control size="sm" value={newBlogPost.Category} onChange={(e) => setNewBlogPost({ ...newBlogPost, Category: e.target.value })} placeholder="e.g. Web Dev" />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Excerpt (shown on the listing card)</Form.Label>
                <Form.Control size="sm" value={newBlogPost.Excerpt} onChange={(e) => setNewBlogPost({ ...newBlogPost, Excerpt: e.target.value })} placeholder="One-sentence hook" />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.8rem' }}>Body (HTML)</Form.Label>
                <Form.Control as="textarea" rows={8} size="sm" value={newBlogPost.Body} onChange={(e) => setNewBlogPost({ ...newBlogPost, Body: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.8rem' }}>Cover Image</Form.Label>
                <Form.Control size="sm" type="file" accept="image/*" onChange={(e) => setNewBlogPostImage((e.target as HTMLInputElement).files?.[0] || null)} />
              </Form.Group>
              <Button type="submit" size="sm" variant="success" disabled={savingBlogPost}>
                {savingBlogPost ? 'Publishing...' : 'Publish Post'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      ) : null}

      {loadingBlogPosts ? <p>Loading blog posts...</p> : null}

      {!loadingBlogPosts && blogPosts.length > 0 ? (
        <Table striped bordered hover variant="dark" responsive size="sm">
          <thead>
            <tr>
              <th>Cover</th>
              <th>Title</th>
              <th>Category</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogPosts.map((post) => (
              <Fragment key={post._id}>
                <tr>
                  <td style={{ width: '72px' }}>
                    {post.Image ? (
                      <img src={post.Image} alt={post.Title} style={{ width: '56px', height: '38px', objectFit: 'cover', borderRadius: '4px' }} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{post.Title}</td>
                  <td>{post.Category || '—'}</td>
                  <td><small>{post.datee}</small></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Button size="sm" variant="outline-light" onClick={() => handleStartEditBlogPost(post)} style={{ marginRight: '0.4rem' }}>
                      {editingBlogId === post._id ? 'Editing...' : 'Edit'}
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDeleteBlogPost(post._id)}>Delete</Button>
                  </td>
                </tr>
                {editingBlogId === post._id ? (
                  <tr>
                    <td colSpan={5}>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSaveBlogPost(post._id);
                        }}
                      >
                        <Row>
                          <Col md={8}>
                            <Form.Group className="mb-2">
                              <Form.Label style={{ fontSize: '0.8rem' }}>Title</Form.Label>
                              <Form.Control size="sm" value={blogEditForm.Title} onChange={(e) => setBlogEditForm({ ...blogEditForm, Title: e.target.value })} required />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-2">
                              <Form.Label style={{ fontSize: '0.8rem' }}>Category</Form.Label>
                              <Form.Control size="sm" value={blogEditForm.Category} onChange={(e) => setBlogEditForm({ ...blogEditForm, Category: e.target.value })} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: '0.8rem' }}>Excerpt</Form.Label>
                          <Form.Control size="sm" value={blogEditForm.Excerpt} onChange={(e) => setBlogEditForm({ ...blogEditForm, Excerpt: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: '0.8rem' }}>Body (HTML)</Form.Label>
                          <Form.Control as="textarea" rows={8} size="sm" value={blogEditForm.Body} onChange={(e) => setBlogEditForm({ ...blogEditForm, Body: e.target.value })} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ fontSize: '0.8rem' }}>Replace Cover Image (optional)</Form.Label>
                          <Form.Control size="sm" type="file" accept="image/*" onChange={(e) => setBlogEditImage((e.target as HTMLInputElement).files?.[0] || null)} />
                        </Form.Group>
                        <Button type="submit" size="sm" variant="success" style={{ marginRight: '0.4rem' }} disabled={savingBlogPost}>
                          {savingBlogPost ? 'Saving...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline-light" onClick={handleCancelEditBlogPost}>Cancel</Button>
                      </Form>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </Table>
      ) : null}

      {!loadingBlogPosts && blogPosts.length === 0 ? (
        <Alert variant="secondary">No posts published from the admin yet — use “+ New Post” above.</Alert>
      ) : null}
    </Container>
  );
};

export default Admin;
